import { getOperatingDateStr } from '../../lib/utils';
import { notifyClubStaff } from '../../lib/notifications';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useBackButton } from '../../hooks/useBackButton';
import {
  Calendar,
  Phone,
  MapPin,
  Clock,
  MessageSquare,
  MessageCircle,
  Check,
  X,
  CheckCircle2,
  AlertCircle,
  User,
  Plus,
  History,
  CalendarDays,
  CalendarCheck,
  Sparkles,
  Search,
  ChevronRight,
  TrendingUp,
  HelpCircle,
  ArrowLeft,
  Trash2,
  Pin,
  Users,
  CreditCard,
  Handshake
} from 'lucide-react';
import {
  collection,
  doc,
  onSnapshot,
  addDoc,
  query,
  where,
  getDocs,
  runTransaction,
  deleteDoc,
  setDoc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ClubSchedule, Booking, BookingRequest, Player, Config, OffDay } from '../../types';
import { getRequiredDepositPercentage } from '../../lib/paymentUtils';
import { logActivity } from '../../services/activityLogger';
import { cancelAssociatedMatch } from '../../lib/matchmakingUtils';
import { PublicMatchCreatorModal } from '../matchmaking/PublicMatchCreatorModal';
import { WhatsAppVerificationModal } from '../common/WhatsAppVerificationModal';
import { FadeInImage } from '../common/FadeInImage';

const CancelPendingBookingButton = ({ item, db, getCollectionPath }: { item: any, db: any, getCollectionPath: any }) => {
  const [startTime] = useState(() => {
    if (item.createdAt) {
      if (typeof item.createdAt === 'number') return item.createdAt;
      if (typeof item.createdAt.toMillis === 'function') return item.createdAt.toMillis();
      if (item.createdAt.seconds) return item.createdAt.seconds * 1000;
    }
    
    // Fallback: If createdAt is missing or not fully synced yet, store it locally so it doesn't reset on remount
    const cacheKey = `bookingReq_start_${item.id}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) return parseInt(cached, 10);
    
    const now = Date.now();
    localStorage.setItem(cacheKey, now.toString());
    return now;
  });

  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, 60 - Math.floor((Date.now() - startTime) / 1000)));
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = Math.max(0, 60 - Math.floor((Date.now() - startTime) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  const isDisabled = timeLeft > 0 || isDeleting;

  return (
    <button
      disabled={isDisabled}
      onClick={async () => {
        if (await window.confirm('¿Seguro que deseas cancelar esta solicitud?')) {
          setIsDeleting(true);
          try {
            const { deleteDoc, doc } = await import('firebase/firestore');
            await deleteDoc(doc(db, getCollectionPath('bookingRequests'), item.id));
          } catch (error) {
            console.error('Error cancelling request', error);
            setIsDeleting(false);
          }
        }
      }}
      className={`flex-[1] text-[10px] font-black py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-1.5 border ${
        isDisabled 
          ? 'bg-zinc-800/50 text-zinc-500 border-zinc-700/50 cursor-not-allowed' 
          : 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/30 hover:scale-[1.02]'
      }`}
    >
      Cancelar Solicitud {timeLeft > 0 && `(${timeLeft}s)`}
    </button>
  );
};

const CancelConfirmedBookingButton = ({ item, db, getCollectionPath }: { item: any, db: any, getCollectionPath: any }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const now = new Date();
  
  const [yyyy, mm, dd] = item.date.split('-');
  const [hours, minutes] = item.timeSlot.split(':');
  const bookingDate = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd), parseInt(hours), parseInt(minutes));
  
  const msDiff = bookingDate.getTime() - now.getTime();
  const hoursLeft = msDiff / (1000 * 60 * 60);
  const canCancel = hoursLeft >= 24;

  const handleCancel = async () => {
    if (!canCancel) {
      alert("No puedes cancelar faltando menos de 24hs. Comunicate con el club por chat privado o por whatsapp.");
      return;
    }
    
    if (await window.confirm('¿Seguro que deseas cancelar este turno?')) {
      setIsDeleting(true);
      try {
        const { updateDoc, doc } = await import('firebase/firestore');
        const bookingRef = doc(db, getCollectionPath('bookings'), item.id);
        await updateDoc(bookingRef, {
          status: 'cancelled',
          updatedAt: Date.now(),
          cancelledBy: 'player'
        });
        const { cancelAssociatedMatch } = await import('../../lib/matchmakingUtils');
        await cancelAssociatedMatch(item.id);
      } catch (error) {
        console.error('Error cancelling booking', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return (
    <button
      disabled={isDeleting}
      onClick={handleCancel}
      className={`flex-[1] text-[10px] font-black py-1.5 rounded-lg transition-all duration-300 flex flex-col items-center justify-center border ${
        canCancel
          ? 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/30 hover:scale-[1.02]'
          : 'bg-[#1a1c23] text-zinc-500 border-zinc-700/50 cursor-pointer hover:bg-[#222530]'
      }`}
    >
      <span>Cancelar</span>
      <span className="text-[7.5px] font-medium opacity-80 lowercase">con más de 24hs de antelación</span>
    </button>
  );
};

const getLocalDateStr = (date: Date = new Date()) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

interface MyBookingsProps {
  currentUser: {
    uid: string;
    email: string;
    name?: string;
    photoUrl?: string;
  };
  players: Player[];
  db: any;
  getCollectionPath: (col: string) => string;
  config: Config;
  sedesPublicas: {
    uid: string;
    name: string;
    photoUrl?: string;
    address?: string;
  }[];
  initialSubTab?: 'grilla' | 'mis-turnos' | 'mis-peticiones';
  onSubTabChange?: (tab: 'grilla' | 'mis-turnos' | 'mis-peticiones') => void;
  onOpenChatWithClub?: (clubId: string) => void;
  onRequireLogin?: () => void;
}

const getFormattedDateWithDay = (dateStr: string) => {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const date = new Date(year, month, day, 0, 0, 0, 0);
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const dayName = days[date.getDay()];
    return `${dayName} ${day}/${month + 1}`;
  } catch (e) {
    return dateStr.split('-').reverse().join('/');
  }
};

export const checkIfSlotBlockedByOffDay = (
  offDays: OffDay[] | undefined,
  date: string,
  court: string,
  time: string,
  dayOfWeek: number
) => {
  if (!offDays || offDays.length === 0) return { isBlocked: false, reason: '' };

  for (const od of offDays) {
    const isMatchingDate = od.date === date;
    const isMatchingDayOfWeek = od.dayOfWeek !== undefined && od.dayOfWeek === dayOfWeek;
    
    if (isMatchingDate || isMatchingDayOfWeek) {
      const isCourtAffected = !od.courts || od.courts.length === 0 || od.courts.includes(court);
      
      if (isCourtAffected) {
        if (od.allDay) {
          return { isBlocked: true, reason: od.reason || 'Día Off' };
        }
        
        if (od.startHour && od.endHour) {
          const [sh, sm] = od.startHour.split(':').map(Number);
          const [eh, em] = od.endHour.split(':').map(Number);
          const [th, tm] = time.split(':').map(Number);
          
          const startMin = sh * 60 + sm;
          let endMin = eh * 60 + em;
          if (endMin <= startMin) endMin += 1440;
          
          const timeMin = th * 60 + tm;
          if (timeMin >= startMin && timeMin < endMin) {
            return { isBlocked: true, reason: od.reason || 'Inhabilitación Parcial' };
          }
        }
      }
    }
  }
  return { isBlocked: false, reason: '' };
};

export function MyBookings({
  currentUser,
  players,
  db,
  getCollectionPath,
  config,
  sedesPublicas,
  initialSubTab,
  onOpenChatWithClub,
  onSubTabChange,
  onRequireLogin
}: MyBookingsProps) {
  // --- ESTADOS PRINCIPALES ---
  const [selectedSedeId, setSelectedSedeId] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const urlClubId = params.get('clubId');
      if (urlClubId) return urlClubId;

      const stored = localStorage.getItem('preselectedSedeId');
      if (stored) {
        localStorage.removeItem('preselectedSedeId');
        return stored;
      }
    }
    return '';
  });
  
  // Si el selectedSedeId es un alias/slug de URL, lo convertimos a su UID real en cuanto cargan las sedes
  useEffect(() => {
    if (selectedSedeId && sedesPublicas.length > 0) {
      const matchBySlug = sedesPublicas.find(s => s.name && s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '') === selectedSedeId);
      if (matchBySlug && matchBySlug.uid !== selectedSedeId) {
        setSelectedSedeId(matchBySlug.uid);
      }
    }
  }, [selectedSedeId, sedesPublicas]);

  const [selectedBookingForMatch, setSelectedBookingForMatch] = useState<any>(null);
  const [myPublicMatches, setMyPublicMatches] = useState<any[]>([]);
  const [matchToDelete, setMatchToDelete] = useState<any | null>(null);

  // --- FILTROS Y ESTADOS DEL HEADER ---
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });

  const [activeSubTab, setActiveSubTab] = useState<'grilla' | 'mis-turnos' | 'mis-peticiones'>(initialSubTab || 'grilla');

  useEffect(() => {
    if (initialSubTab) {
      setActiveSubTab(initialSubTab);
    }
  }, [initialSubTab]);

  useEffect(() => {
    if (onSubTabChange) {
      onSubTabChange(activeSubTab);
    }
  }, [activeSubTab]);

  const [showMyRequests, setShowMyRequests] = useState(false);
  const [highlightedTurnoId, setHighlightedTurnoId] = useState<string | null>(null);

  useBackButton(!!selectedSedeId, () => {
    setSelectedSedeId('');
    setSearchSedeQuery('');
  });

  const [showAllHistoryModal, setShowAllHistoryModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // --- GUEST LOCKING & DEFERRED LOGIN ---
  const [guestSessionId] = useState(() => 'guest_' + Math.random().toString(36).substring(2, 11));
  const [pendingBookingSubmit, setPendingBookingSubmit] = useState(false);



  // --- DATOS REAL-TIME DE LA SEDE SELECCIONADA ---
  const [schedule, setSchedule] = useState<ClubSchedule | null>(null);
  const todayStr = React.useMemo(() => getOperatingDateStr(new Date(), schedule?.cashRegisterCloseTime || '03:30'), [schedule?.cashRegisterCloseTime]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [requests, setRequests] = useState<BookingRequest[]>([]);
  const [clubPendingRequests, setClubPendingRequests] = useState<BookingRequest[]>([]);
  const [activeLocks, setActiveLocks] = useState<any[]>([]);
  const [myActiveLockId, setMyActiveLockId] = useState<string | null>(null);
  const [lockingSlot, setLockingSlot] = useState<{ court: string; time: string } | null>(null);
  const [lockTimeLeft, setLockTimeLeft] = useState<number>(90);

  // --- ESTADOS DE LA MODAL DE SOLICITUD ---
  const [selectedSlot, setSelectedSlot] = useState<{
    court: string;
    time: string;
    booking?: Booking;
  } | null>(null);

  const [option2Court, setOption2Court] = useState<string>('');
  const [option2Time, setOption2Time] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [sendingRequest, setSendingRequest] = useState(false);

  // --- ESTADOS DE DECLARACIONES Y CONFIRMACIÓN ---
  const [checkAdminCourt, setCheckAdminCourt] = useState(false);
  const [checkCancelPolicy, setCheckCancelPolicy] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');

  const [tick, setTick] = useState(0);

  // --- ESTADOS DE MODALES DE CANCELACIÓN (NUEVO) ---
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showCancelFixedModal, setShowCancelFixedModal] = useState(false);
  const [targetFixedTurn, setTargetFixedTurn] = useState<Booking | null>(null);
  const [payingBookingId, setPayingBookingId] = useState<string | null>(null);

  const handlePayDepositForRequest = async (req: any) => {
    try {
      setPayingBookingId(req.id);
      
      const option = req.selectedOptionForDeposit || req.option1;
      const bookingId = `${req.clubId}_${option.courtName.replace(/\s+/g, '')}_${req.date}_${option.timeSlot.replace(':', '')}`;
      const bookingDocRef = doc(db, getCollectionPath('bookings'), bookingId);
      const reqDocRef = doc(db, getCollectionPath('bookingRequests'), req.id);

      await runTransaction(db, async (transaction) => {
        const bSnap = await transaction.get(bookingDocRef);
        if (bSnap.exists() && bSnap.data()?.status !== 'promo') {
          const status = bSnap.data()?.status;
          if (status === 'cancelled' || status === 'rejected') {
            const archiveId = `${bookingId}_archived_${Date.now()}`;
            const archiveRef = doc(db, getCollectionPath('bookings'), archiveId);
            transaction.set(archiveRef, { ...bSnap.data(), id: archiveId, archivedAt: Date.now() });
          } else {
            throw new Error('COLLISION');
          }
        }

        const bData = {
            id: bookingId,
            fromRequest: true,
            clubId: req.clubId,
          courtName: option.courtName,
          date: req.date,
          timeSlot: option.timeSlot,
          duration: 90,
          status: 'pending',
          isFixed: false,
          notes: req.notes || '',
          price: req.price || 12000,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          playerId: currentUser.uid,
          playerName: currentPlayerProfile?.name || currentUser.name || currentUser.email.split('@')[0],
          playerCategory: currentPlayerProfile?.category || 'Cat -',
          playerPhoto: currentPlayerProfile?.photoUrl || currentUser.photoUrl || '',
          paymentStatus: 'pending',
          paymentExpiresAt: Date.now() + 5 * 60 * 1000,
          confirmationStatus: 'pending_confirmation',
          requiredDepositPercentage: req.requiredDepositPercentage
        };

        if (bSnap.exists() && bSnap.data()?.status === 'promo') {
          transaction.delete(bookingDocRef);
        }
        transaction.set(bookingDocRef, bData);
        
        transaction.update(reqDocRef, { status: 'accepted' });
      });

      const functionsInstance = getFunctions();
      const createPreference = httpsCallable(functionsInstance, 'createMercadoPagoPreference');
      const result = await createPreference({
        bookingId,
        clubId: req.clubId,
        origin: window.location.origin
      });

      const data = result.data as any;
      if (data && data.initPoint) {
        window.location.href = data.initPoint;
      } else {
        alert("No se pudo generar el enlace de pago.");
        setPayingBookingId(null);
      }

    } catch (e: any) {
      console.error(e);
      if (e.message === 'COLLISION') {
        alert("Lo sentimos, este turno acaba de ser ocupado. No se puede proceder al pago.");
      } else {
        alert("Ocurrió un error al procesar la seña.");
      }
      setPayingBookingId(null);
    }
  };

  const handlePayRequestDeposit = async (req: any) => {
    try {
      setPayingBookingId(req.id);
      
      const option = req.option1;
      if (!option) throw new Error("Missing option");

      // Generate a clean bookingId similar to handleResolveRequest
      const bookingId = `${req.clubId}_${option.courtName.replace(/\s+/g, '')}_${req.date}_${option.timeSlot.replace(':', '')}`;
      const { doc, runTransaction } = await import('firebase/firestore');
      const bookingDocRef = doc(db, getCollectionPath('bookings'), bookingId);
      const reqRef = doc(db, getCollectionPath('bookingRequests'), req.id);

      await runTransaction(db, async (transaction) => {
        const bSnap = await transaction.get(bookingDocRef);
        if (bSnap.exists() && bSnap.data()?.status !== 'promo') {
          const status = bSnap.data()?.status;
          if (status === 'cancelled' || status === 'rejected') {
            const archiveId = `${bookingId}_archived_${Date.now()}`;
            const archiveRef = doc(db, getCollectionPath('bookings'), archiveId);
            transaction.set(archiveRef, { ...bSnap.data(), id: archiveId, archivedAt: Date.now() });
          } else {
            throw new Error('COLLISION');
          }
        }

        const newBookingData = {
            id: bookingId,
            fromRequest: true,
            clubId: req.clubId || '',
          playerId: currentUser?.uid || '',
          playerName: currentUser?.name || 'Jugador',
          playerCategory: '',
          playerPhoto: currentUser?.photoUrl || '',
          courtName: option.courtName,
          date: req.date,
          timeSlot: option.timeSlot,
          price: req.price || 12000,
          status: 'pending',
          paymentStatus: 'pending',
          paymentExpiresAt: Date.now() + 10 * 60 * 1000,
          confirmationStatus: 'pending_confirmation',
          requiredDepositPercentage: req.requiredDepositPercentage || 25,
          createdAt: Date.now(),
          notes: req.notes || '',
          assignedByClub: false,
          playerConfirmed: false,
          isFixed: false
        };

        transaction.set(bookingDocRef, newBookingData);
        transaction.delete(reqRef);
      });

      // Execute MercadoPago payment flow on the newly created booking
      const success = await handlePayBooking(bookingId, req.clubId);
      if (!success) {
        alert("No se pudo generar el link de pago.");
      }
    } catch (e: any) {
      if (e.message === 'COLLISION') {
        alert("Ese casillero ya fue ocupado mientras esperábamos.");
      } else {
        alert("Error al procesar el pago: " + e.message);
      }
    } finally {
      setPayingBookingId(null);
    }
  };

  const handlePayBooking = async (bookingId: string, clubId: string): Promise<boolean> => {
    if (!bookingId || !clubId) return false;
    try {
      setPayingBookingId(bookingId);
      const functionsInstance = getFunctions();
      const createPreference = httpsCallable(functionsInstance, 'createMercadoPagoPreference');
      
      const result = await createPreference({
        bookingId,
        clubId,
        origin: window.location.origin
      });

      const data = result.data as any;
      if (data && data.initPoint) {
        window.location.href = data.initPoint;
        return true;
      } else {
        alert("No se pudo generar el enlace de pago. Inténtalo de nuevo.");
        return false;
      }
    } catch (err: any) {
      console.error("Error al iniciar pago:", err);
      alert("Error al iniciar el pago con Mercado Pago: " + (err.message || err));
      return false;
    } finally {
      setPayingBookingId(null);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // --- LIMPIEZA DE BLOQUEO TEMPORAL AL DESMONTAR O CERRAR PESTAÑA ---
  const myActiveLockIdRef = useRef<string | null>(null);
  useEffect(() => {
    myActiveLockIdRef.current = myActiveLockId;
  }, [myActiveLockId]);

  useEffect(() => {
    return () => {
      if (myActiveLockIdRef.current) {
        const lockId = myActiveLockIdRef.current;
        const lockRef = doc(db, getCollectionPath('slotLocks'), lockId);
        deleteDoc(lockRef).catch(e => console.error("Error on unmount lock cleanup:", e));
      }
    };
  }, [db, getCollectionPath]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (myActiveLockIdRef.current) {
        const lockId = myActiveLockIdRef.current;
        const lockRef = doc(db, getCollectionPath('slotLocks'), lockId);
        deleteDoc(lockRef).catch(() => { });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [db, getCollectionPath]);

  // --- CONTADOR DE TIEMPO RESTANTE PARA EL BLOQUEO ---
  useEffect(() => {
    if (!selectedSlot || !myActiveLockId) {
      setLockTimeLeft(90);
      return;
    }

    const lock = activeLocks.find(l => l.id === myActiveLockId);
    const expiresAt = lock ? lock.expiresAt : (Date.now() + 90000);

    let timerId: ReturnType<typeof setInterval>;

    const updateTimer = () => {
      const remainingMs = expiresAt - Date.now();
      const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
      setLockTimeLeft(remainingSec);

      if (remainingMs <= 0 && timerId) {
        clearInterval(timerId);
      }
    };

    updateTimer();
    timerId = setInterval(updateTimer, 1000);

    return () => clearInterval(timerId);
  }, [selectedSlot, myActiveLockId, activeLocks]);

  // --- CERRAR MODAL Y LIBERAR EL BLOQUEO TEMPORAL ---
  const closeModalAndReleaseLock = async () => {
    if (myActiveLockId) {
      try {
        const lockRef = doc(db, getCollectionPath('slotLocks'), myActiveLockId);
        await deleteDoc(lockRef);
      } catch (e) {
        console.error("Error releasing lock:", e);
      }
      setMyActiveLockId(null);
    }
    setSelectedSlot(null);
    setOption2Court('');
    setOption2Time('');
    setNotes('');
    setCheckAdminCourt(false);
    setCheckCancelPolicy(false);
    setShowConfirmPopup(false);
  };

  // --- DATOS DE MIS TURNO DE JUGADOR (TODAS LAS SEDES) ---
  const [myConfirmedBookings, setMyConfirmedBookings] = useState<Booking[]>([]);
  const [myRequests, setMyRequests] = useState<BookingRequest[]>([]);

  const [now, setNow] = useState(Date.now());
  const deletedRequestsRef = useRef<Set<string>>(new Set());

  // useEffect para limpiar expirados y actualizar 'now'
  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime = Date.now();
      setNow(currentTime);

      if (myRequests && myRequests.length > 0) {
        myRequests.forEach(async (req) => {
          if (req.status === 'deposit_required' && req.depositExpiresAt && req.depositExpiresAt <= currentTime) {
            if (!deletedRequestsRef.current.has(req.id)) {
              deletedRequestsRef.current.add(req.id);
              try {
                await deleteDoc(doc(db, getCollectionPath('bookingRequests'), req.id));
                
                await addDoc(collection(db, getCollectionPath('notifications')), {
                  userId: req.playerId,
                  title: 'Tiempo de seña expirado',
                  body: "Se ha terminado el tiempo (10') para señar el turno. Vuelve a contactarte con el club si aun quieres reservar el turno.",
                  type: 'deposit_expired',
                  createdAt: currentTime,
                  url: '/?tab=reservas&subtab=mis-turnos'
                });
              } catch (e) {
                console.error("Error deleting expired request", e);
                deletedRequestsRef.current.delete(req.id);
              }
            }
          }
        });
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [myRequests]);

  useEffect(() => {
    if (!selectedSlot) {
      setCheckAdminCourt(false);
      setCheckCancelPolicy(false);
      setShowConfirmPopup(false);
    }
  }, [selectedSlot]);

  // --- REFS PARA LOS SELECTORES DE FECHA ---
  const desktopDatePickerRef = useRef<HTMLInputElement>(null);
  const mobileDatePickerRef = useRef<HTMLInputElement>(null);

  const handleOpenDesktopPicker = () => {
    if (desktopDatePickerRef.current) {
      try {
        desktopDatePickerRef.current.showPicker();
      } catch (err) {
        desktopDatePickerRef.current.click();
      }
    }
  };

  const handleOpenMobilePicker = () => {
    if (mobileDatePickerRef.current) {
      try {
        mobileDatePickerRef.current.showPicker();
      } catch (err) {
        mobileDatePickerRef.current.click();
      }
    }
  };

  // --- BUSCADOR DE SEDE ---
  const [searchSedeQuery, setSearchSedeQuery] = useState('');
  const [showSedeDropdown, setShowSedeDropdown] = useState(false);

  // --- PERFIL DEL JUGADOR LOGUEADO ---
  // --- PERFIL DEL JUGADOR LOGUEADO ---
  const currentPlayerProfile = useMemo(() => {
    return players.find(p => p.id === currentUser.uid) || null;
  }, [players, currentUser.uid]);

  // Manejo del deferred login para invitados
  useEffect(() => {
    if (currentUser?.uid && pendingBookingSubmit) {
      setPendingBookingSubmit(false);
      const isPhoneVerified = currentPlayerProfile?.phoneVerified || (currentUser as any)?.phoneVerified || false;
      if (!isPhoneVerified) {
        setShowPhoneModal(true);
      } else {
        handleCreateRequest();
      }
    }
  }, [currentUser, pendingBookingSubmit, currentPlayerProfile]);

  // Sede seleccionada por ID
  const selectedSede = useMemo(() => {
    return sedesPublicas.find(s => s.uid === selectedSedeId) || null;
  }, [sedesPublicas, selectedSedeId]);

  // Sedes filtradas por query de búsqueda
  const filteredSedes = useMemo(() => {
    if (!searchSedeQuery.trim()) return sedesPublicas;
    return sedesPublicas.filter(s => s.name.toLowerCase().includes(searchSedeQuery.toLowerCase()));
  }, [sedesPublicas, searchSedeQuery]);

  // --- GENERACIÓN DE DÍAS Y LOGICA DE NAVEGACIÓN DE FECHAS (FORMATO INTUITIVO DE USUARIO) ---
  const todayDateStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const tomorrowDateStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const maxDateStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const desktopDays = useMemo(() => {
    const days = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const current = new Date(today);
      current.setDate(today.getDate() + i);
      const yyyy = current.getFullYear();
      const mm = String(current.getMonth() + 1).padStart(2, '0');
      const dd = String(current.getDate()).padStart(2, '0');
      const dateStr = `${yyyy}-${mm}-${dd}`;

      let label = '';
      if (i === 0) {
        label = 'Hoy';
      } else if (i === 1) {
        label = 'Mañana';
      } else {
        const weekdayStr = current.toLocaleDateString('es-ES', { weekday: 'short' });
        const dayNum = current.getDate();
        const cleanWeekday = weekdayStr.replace('.', '');
        const capitalized = cleanWeekday.charAt(0).toUpperCase() + cleanWeekday.slice(1);
        label = `${capitalized} ${dayNum}`;
      }

      days.push({
        dateStr,
        label
      });
    }
    return days;
  }, []);

  const isDateInDesktopDays = useMemo(() => {
    return desktopDays.some(day => day.dateStr === selectedDate);
  }, [selectedDate, desktopDays]);

  const formattedCustomDate = useMemo(() => {
    if (!selectedDate) return '';
    const parts = selectedDate.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}`; // DD/MM
    }
    return selectedDate;
  }, [selectedDate]);

  // --- ESCUCHAR DATOS REAL-TIME DE LA SEDE SELECCIONADA ---
  useEffect(() => {
    if (!selectedSedeId) return;

    setLoading(true);

    // 1. Horario / Configuración de Canchas
    const scheduleRef = doc(db, getCollectionPath('clubSchedules'), selectedSedeId);
    const unsubSchedule = onSnapshot(scheduleRef, (docSnap) => {
      if (docSnap.exists()) {
        setSchedule(docSnap.data() as ClubSchedule);
      } else {
        setSchedule(null);
      }
      setLoading(false);
    });

    // 2. Reservas del Club (Normales y Fijas)
    const bookingsRef = collection(db, getCollectionPath('bookings'));
    
    const qBookings = query(
      bookingsRef,
      where('date', '==', selectedDate),
      where('clubId', '==', selectedSedeId)
    );

    const qFixed = query(
      bookingsRef,
      where('clubId', '==', selectedSedeId),
      where('isFixed', '==', true)
    );

    let currentDirect: Booking[] = [];
    let currentFixed: Booking[] = [];

    const updateBookings = () => {
      const selectedDayOfWeek = new Date(selectedDate + 'T00:00:00').getDay();
      
      const validDirect = currentDirect.filter(b => b.status !== 'cancelled' && !b.hiddenFromGrid);
      
      const projected = currentFixed.filter(b => 
        (!b.id.includes('_materialized_') || b.isFixedRoot) &&
        !b.id.includes('_cancelled_') &&
        b.status !== 'cancelled' &&
        new Date(b.date + 'T00:00:00').getDay() === selectedDayOfWeek &&
        selectedDate >= b.date &&
        (!b.excludedDates || !b.excludedDates.includes(selectedDate)) &&
        !b.hiddenFromGrid &&
        !validDirect.some(d => d.courtName === b.courtName && d.timeSlot === b.timeSlot)
      ).map(b => ({ ...b, date: selectedDate })); // Proyectamos a la fecha seleccionada

      setBookings([...validDirect, ...projected]);
    };

    const unsubBookings = onSnapshot(qBookings, (snap) => {
      const bList: Booking[] = [];
      snap.forEach((d) => {
        bList.push({ ...d.data(), id: d.id } as Booking);
      });
      currentDirect = bList;
      updateBookings();
    });

    const unsubFixed = onSnapshot(qFixed, (snap) => {
      const bList: Booking[] = [];
      snap.forEach((d) => {
        bList.push({ ...d.data(), id: d.id } as Booking);
      });
      currentFixed = bList;
      updateBookings();
    });
    // 3. Solicitudes de reserva del club para esta fecha (pendientes de confirmación)
    const requestsRef = collection(db, getCollectionPath('bookingRequests'));
    const qRequests = query(
      requestsRef,
      where('clubId', '==', selectedSedeId),
      where('date', '==', selectedDate),
      where('status', 'in', ['pending', 'deposit_required'])
    );
    const unsubRequests = onSnapshot(qRequests, (snap) => {
      const rList: BookingRequest[] = [];
      snap.forEach((d) => {
        const r = d.data() as BookingRequest;
        rList.push({ ...r, id: d.id });
      });
      setClubPendingRequests(rList);
    });

    // 4. Bloqueos temporales del club para esta fecha
    const locksRef = collection(db, getCollectionPath('slotLocks'));
    const qLocks = query(
      locksRef,
      where('clubId', '==', selectedSedeId),
      where('date', '==', selectedDate)
    );
    const unsubLocks = onSnapshot(qLocks, (snap) => {
      const lList: any[] = [];
      snap.forEach((d) => {
        const l = d.data();
        lList.push({ ...l, id: d.id });
      });
      setActiveLocks(lList);
    });

    return () => {
      unsubSchedule();
      unsubBookings();
      unsubFixed();
      unsubRequests();
      unsubLocks();
    };
  }, [selectedSedeId, selectedDate, db, getCollectionPath]);

  // --- ESCUCHAR MIS RESERVAS Y MIS PETICIONES GLOBALES ---
  useEffect(() => {
    if (!currentUser.uid) return;

    // Escuchar mis reservas (bookings donde playerId es el usuario conectado)
    const bookingsRef = collection(db, getCollectionPath('bookings'));
    const qMyBookings = query(bookingsRef, where('playerId', '==', currentUser.uid));
    const unsubMyBookings = onSnapshot(qMyBookings, (snap) => {
      const list: Booking[] = [];
      snap.forEach((d) => {
        list.push({ ...d.data(), id: d.id } as Booking);
      });
      // Ordenar por fecha y luego horario de forma descendiente
      list.sort((a, b) => {
        const dateCompare = b.date.localeCompare(a.date);
        if (dateCompare !== 0) return dateCompare;
        return b.timeSlot.localeCompare(a.timeSlot);
      });
      setMyConfirmedBookings(list);
    });

    // Escuchar mis peticiones (bookingRequests de este jugador)
    const requestsRef = collection(db, getCollectionPath('bookingRequests'));
    const qMyRequests = query(requestsRef, where('playerId', '==', currentUser.uid));
    const unsubMyRequests = onSnapshot(qMyRequests, (snap) => {
      const list: BookingRequest[] = [];
      snap.forEach((d) => {
        list.push({ ...d.data(), id: d.id } as BookingRequest);
      });
      // Ordenar por fecha descendente
      list.sort((a, b) => b.date.localeCompare(a.date));
      setMyRequests(list);
    });

    // Escuchar mis partidos publicados activos
    const matchesRef = collection(db, getCollectionPath('public_matches'));
    const qMyMatches = query(
      matchesRef,
      where('creatorId', '==', currentUser.uid),
      where('status', 'in', ['open', 'pending_opponent_validation', 'pending_validation', 'disputed', 'finished', 'completed'])
    );
    const unsubMyMatches = onSnapshot(qMyMatches, (snap) => {
      const list: any[] = [];
      snap.forEach((d) => {
        list.push({ ...d.data(), id: d.id });
      });
      setMyPublicMatches(list);
    });

    return () => {
      unsubMyBookings();
      unsubMyRequests();
      unsubMyMatches();
    };
  }, [currentUser.uid, db, getCollectionPath]);


  // --- HISTORIAL DE RESERVAS (PRESERVADO COMPLETAMENTE SIN AUTO-LIMPIEZA) ---
  // Se ha removido la auto-limpieza para garantizar que no se borren de forma automática 
  // las reservas o solicitudes del jugador, permitiendo que el administrador las vea y gestione siempre.

  // --- MEMO PARA UNIFICAR MIS TURNOS Y MIS SOLICITUDES EN UN ÚNICO HISTORIAL ---
  const combinedTurnos = useMemo(() => {
    // 1. Convertir bookings confirmados/pagados a formato unificado
    const bookingsList = myConfirmedBookings
      .filter(b => !b.isFixed || !b.excludedDates || !b.excludedDates.includes(b.date))
      .map(b => ({
        id: b.id,
        type: 'booking' as const,
        clubId: b.clubId,
        courtName: b.courtName,
        date: b.date,
        timeSlot: b.timeSlot, promoDiscountPercentage: b.promoDiscountPercentage, promoPerks: b.promoPerks,
        price: (b.price || 12000) + (b.items?.reduce((s, item) => s + (item.price * item.quantity), 0) || 0),
        status: b.status, // 'confirmed', 'paid', 'pending'
        paymentStatus: b.paymentStatus,
        notes: b.notes || '',
        adminNote: b.adminNote || '',
        option1: null,
        option2: null,
        assignedByClub: b.assignedByClub || false,
        playerConfirmed: b.playerConfirmed,
        isFixed: b.isFixed || false,
        confirmationRequested: b.confirmationRequested || false,
      }));

    // 2. Convertir peticiones (solo las 'pending' y 'rejected') para no duplicar las ya aceptadas/convertidas a bookings
    const requestsList = myRequests
      .filter(r => (r.status === 'pending' || r.status === 'rejected' || r.status === 'deposit_required') && (!r.depositExpiresAt || r.depositExpiresAt > now))
      .map(r => ({
        id: r.id,
        type: 'request' as const,
        clubId: r.clubId,
        courtName: r.option1?.courtName || 'Cancha',
        date: r.date,
        timeSlot: r.option1?.timeSlot || '00:00',
        price: r.price || 12000,
        status: r.status, // 'pending' o 'rejected'
        notes: r.notes || '',
        adminNote: r.adminNote || '',
        option1: r.option1,
        option2: r.option2,
        requiredDepositPercentage: r.requiredDepositPercentage,
        depositExpiresAt: r.depositExpiresAt
      }));

    // 3. Fusionar y ordenar descendente (más nuevas primero)
    const merged = [...bookingsList, ...requestsList];
    merged.sort((a, b) => {
      const dateCompare = b.date.localeCompare(a.date);
      if (dateCompare !== 0) return dateCompare;
      return b.timeSlot.localeCompare(a.timeSlot);
    });

    return merged;
  }, [myConfirmedBookings, myRequests]);

  const nextFixedTurns = useMemo(() => {
    const today = getOperatingDateStr(new Date(), schedule?.cashRegisterCloseTime || '03:30');
    const result: Booking[] = [];

    // Agrupar por series fijas para proyectar la próxima ocurrencia pendiente
    const fixedSeries = myConfirmedBookings.filter(b => b.isFixed && b.status !== 'cancelled' && (!b.id.includes('_materialized_') || b.isFixedRoot) && !b.id.includes('_cancelled_'));

    fixedSeries.forEach(booking => {
      let targetDate = booking.date;
      let isProjected = false;

      // Si el turno actual ya pasó, o si ya está confirmado/pagado para su fecha actual, 
      // proyectamos a la siguiente semana para que el usuario siempre vea "lo que sigue".
      if (targetDate < today || booking.playerConfirmed || booking.status === 'paid' || (booking.status === 'confirmed' && booking.playerConfirmed !== false)) {
        const rootDate = new Date(booking.date + 'T00:00:00');
        let nextDate = new Date(rootDate);

        // Avanzar semanas hasta encontrar la próxima fecha válida
        while (true) {
          const nextDateStr = getLocalDateStr(nextDate);

          // 1. Verificar si ya existe alguna reserva (materializada o raíz) para esta fecha/cancha/hora que ya esté confirmada o pagada
          const isAlreadyHandled = myConfirmedBookings.some(b =>
            b.date === nextDateStr &&
            b.courtName === booking.courtName &&
            b.timeSlot === booking.timeSlot &&
            (b.playerConfirmed || b.status === 'paid' || b.status === 'confirmed')
          );

          // 2. REGLA ESPECIAL: Si la fecha coincide exactamente con la del documento raíz fijo, 
          // la consideramos "ya gestionada" (ya confirmada al crear el turno fijo) y pasamos a la siguiente.
          const isRootDate = nextDateStr === booking.date;

          const isExcluded = booking.excludedDates && booking.excludedDates.includes(nextDateStr);
          const isPast = nextDateStr < today;

          if (isPast || isExcluded || isAlreadyHandled || isRootDate) {
            nextDate.setDate(nextDate.getDate() + 7);
          } else {
            break;
          }
        }
        targetDate = getLocalDateStr(nextDate);
        isProjected = targetDate !== booking.date;
      }

      // Evitar duplicados si ya existe una instancia materializada para esa fecha proyectada
      const alreadyHasEntry = result.some(r => r.date === targetDate && r.courtName === booking.courtName && r.timeSlot === booking.timeSlot);
      const isAlreadyMaterializedInBookings = myConfirmedBookings.some(b =>
        b.date === targetDate &&
        b.courtName === booking.courtName &&
        b.timeSlot === booking.timeSlot &&
        b.id !== booking.id
      );

      if (!alreadyHasEntry) {
        if (isAlreadyMaterializedInBookings) {
          // Si ya existe un documento materializado (ej: admin envió consulta), lo usamos para que el jugador pueda interactuar con él
          const materializedDoc = myConfirmedBookings.find(b => 
            b.date === targetDate && 
            b.courtName === booking.courtName && 
            b.timeSlot === booking.timeSlot && 
            b.id !== booking.id
          );
          if (materializedDoc && materializedDoc.status !== 'cancelled') {
            result.push(materializedDoc);
          }
        } else {
          result.push({
            ...booking,
            date: targetDate,
            // Si es proyectado, el estado de confirmación vuelve a ser falso para la nueva fecha
            playerConfirmed: isProjected ? false : booking.playerConfirmed
          });
        }
      }
    });

    return result.sort((a, b) => a.date.localeCompare(b.date));
  }, [myConfirmedBookings, todayStr]);

  const activeBookingsCount = useMemo(() => {
    const today = getOperatingDateStr(new Date(), schedule?.cashRegisterCloseTime || '03:30');
    // 1. Bookings normales o materializados confirmados en el futuro que no estén pagados ni cancelados
    const activeNormal = myConfirmedBookings.filter(b => 
      b.date >= today && 
      b.status !== 'cancelled' && 
      b.status !== 'paid' &&
      (!b.isFixed || b.id.includes('_materialized_'))
    ).length;

    // 2. Ocurrencias de turnos fijos futuros pendientes de confirmación del jugador
    const activeFixedPending = nextFixedTurns.filter(b => 
      b.date >= today && 
      !b.playerConfirmed
    ).length;

    return activeNormal + activeFixedPending;
  }, [myConfirmedBookings, nextFixedTurns]);

  const pendingRequestsCount = useMemo(() => {
    const today = getOperatingDateStr(new Date(), schedule?.cashRegisterCloseTime || '03:30');
    return myRequests.filter(r => r.status === 'pending' && r.date >= today).length;
  }, [myRequests]);

  const totalMyTurnosCount = activeBookingsCount + pendingRequestsCount;

  // --- OBTENER PRECIO Y DESCUENTO DE UN SLOT ---
  const getSlotPriceAndDiscount = (court: string, time: string, dateStr: string) => {
    if (!schedule) return { originalPrice: 12000, finalPrice: 12000, discount: null };

    const originalPrice = schedule.pricePerSlot || 12000;
    if (!schedule.timeDiscounts || schedule.timeDiscounts.length === 0) {
      return { originalPrice, finalPrice: originalPrice, discount: null };
    }

    const d = new Date(dateStr + 'T00:00:00');
    const dayOfWeek = d.getDay();

    // Buscar el descuento aplicable
    const activeDiscount = schedule.timeDiscounts.find(td => {
      // 1. Verificar día
      if (!td.daysOfWeek.includes(dayOfWeek)) return false;
      // 2. Verificar cancha
      if (td.courts && td.courts.length > 0 && !td.courts.includes(court)) return false;
      // 3. Verificar hora (time es "HH:MM", td.startHour y td.endHour son "HH:MM")
      return time >= td.startHour && time < td.endHour;
    });

    if (!activeDiscount) {
      return { originalPrice, finalPrice: originalPrice, discount: null };
    }

    let finalPrice = originalPrice;
    if (activeDiscount.discountType === 'percentage') {
      finalPrice = Math.max(0, originalPrice * (1 - activeDiscount.value / 100));
    } else {
      finalPrice = Math.max(0, originalPrice - activeDiscount.value);
    }

    return {
      originalPrice,
      finalPrice,
      discount: activeDiscount
    };
  };

  // --- GENERADOR DE INTERVALOS DE TIEMPO (REPLICADO DEL ADMIN) ---
  const timeSlots = useMemo(() => {
    if (!schedule) return [];

    // 1. Obtener día de la semana y fecha seleccionada
    const d = new Date(selectedDate + 'T00:00:00');
    const dayOfWeek = d.getDay(); // 0 = Domingo, 1 = Lunes, etc.

    // 2. Verificar si hay un horario específico configurado para este día
    const specSchedule = schedule.specificDaySchedules?.find(s => s.dayOfWeek === dayOfWeek);
    if (specSchedule && specSchedule.closed) {
      return []; // El club está cerrado este día
    }

    // 3. Obtener horas de apertura y cierre base (ya sea custom o estándar)
    const openStr = specSchedule?.openTime || schedule.openTime;
    const closeStr = specSchedule?.closeTime || schedule.closeTime;

    const [openH, openM] = openStr.split(':').map(Number);
    const [closeH, closeM] = closeStr.split(':').map(Number);
    const duration = schedule.slotDuration || 90;

    let openMinutes = openH * 60 + openM;
    let closeMinutes = closeH * 60 + closeM;

    // Cruce de medianoche
    if (closeMinutes <= openMinutes) {
      closeMinutes += 1440;
    }

    // Obtener la hora y minutos actuales si hoy es el día seleccionado
    const isToday = selectedDate === todayDateStr;
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const slots: string[] = [];
    let current = openMinutes;

    while (current + duration <= closeMinutes) {
      const h = Math.floor(current / 60) % 24;
      const m = current % 60;
      const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;

      // Si es hoy, omitir turnos que ya comenzaron o ya terminaron
      let isPast = false;
      if (isToday) {
        const slotMinutes = h * 60 + m;
        if (slotMinutes < currentMinutes) {
          isPast = true;
        }
      }

      if (!isPast) {
        slots.push(timeStr);
      }

      current += duration;
    }

    return slots;
  }, [schedule, selectedDate, todayDateStr]);

  // --- OBTENER / ADQUIRIR EL BLOQUEO TEMPORAL EN CALIENTE (OPTIMISTIC LOCK) ---
  const handleSlotClick = async (court: string, time: string, promoBooking?: Booking) => {
    const uid = currentUser?.uid || guestSessionId;

    // 1. Verificaciones rápidas locales antes de intentar el bloqueo
    const isBooked = bookings.some(
      (b) => b.clubId === selectedSedeId && b.courtName === court && b.timeSlot === time && b.status !== 'promo'
    );
    if (isBooked) {
      alert('⚠️ Este turno ya está reservado.');
      return;
    }

    const hasPending = clubPendingRequests.some(
      (r) => r.option1?.courtName === court && r.option1?.timeSlot === time
    );
    if (hasPending) {
      alert('⚠️ Este turno ya tiene una solicitud pendiente de revisión.');
      return;
    }

    const hasLock = activeLocks.some(
      (l) => l.courtName === court && l.timeSlot === time && l.expiresAt > Date.now() && l.lockedBy !== uid
    );
    if (hasLock) {
      alert('⚠️ Este turno está siendo reservado por otro jugador en este momento. Por favor, selecciona otro horario.');
      return;
    }

    // 2. Intentar adquirir el bloqueo optimista en caliente
    setLockingSlot({ court, time });
    try {
      const slotId = `${selectedSedeId}_${court.replace(/\s+/g, '')}_${selectedDate}_${time.replace(':', '')}`;
      const lockDocRef = doc(db, getCollectionPath('slotLocks'), slotId);
      const bookingDocRef = doc(db, getCollectionPath('bookings'), slotId);

      await runTransaction(db, async (transaction) => {
        // Verificar si se reservó en este microsegundo
        const bookingSnap = await transaction.get(bookingDocRef);
        if (bookingSnap.exists()) {
          const bData = bookingSnap.data() as Booking;
          if (bData && (bData as any).status !== 'promo' && bData.status !== 'cancelled' && (bData as any).status !== 'rejected') {
            throw new Error("ALREADY_BOOKED");
          }
        }

        // Verificar si se bloqueó en este microsegundo
        const lockSnap = await transaction.get(lockDocRef);
        if (lockSnap.exists()) {
          const lockData = lockSnap.data();
          if (lockData && lockData.expiresAt > Date.now() && lockData.lockedBy !== uid) {
            throw new Error("LOCKED");
          }
        }

        // Escribir el bloqueo temporal por 90 segundos (90,000 ms)
        transaction.set(lockDocRef, {
          clubId: selectedSedeId,
          courtName: court,
          date: selectedDate,
          timeSlot: time,
          lockedBy: uid,
          lockedByName: currentPlayerProfile?.name || currentUser?.name || currentUser?.email?.split('@')[0] || 'Invitado',
          expiresAt: Date.now() + 90000
        });
      });

      // Bloqueo exitoso
      setMyActiveLockId(slotId);
      setSelectedSlot({ court, time, booking: promoBooking });
      setOption2Court('');
      setOption2Time('');
    } catch (err: any) {
      if (err.message === "LOCKED") {
        alert('⏳ Este turno está siendo reservado por otro jugador en este momento. Inténtalo de nuevo en 90 segundos.');
      } else if (err.message === "ALREADY_BOOKED") {
        alert('⚠️ ¡Turno tomado! Este casillero acaba de ser reservado por otro jugador.');
      } else {
        console.error("Error al adquirir bloqueo:", err);
        alert('Error al seleccionar el turno. Inténtalo de nuevo: ' + err.message);
      }
    } finally {
      setLockingSlot(null);
    }
  };

  // --- PROPUESTA DE PETICIÓN DE RESERVA (CREAR SOLICITUD) ---
  const handleCreateRequest = async () => {
    if (!selectedSlot || !selectedSedeId) return;

    setSendingRequest(true);
    try {
      const priceInfo = getSlotPriceAndDiscount(selectedSlot.court, selectedSlot.time, selectedDate);
        const isIndividualPromo = selectedSlot.booking?.status === 'promo';
        const isGeneralPercentagePromo = priceInfo.discount && priceInfo.discount.discountType === 'percentage' && priceInfo.discount.value > 0;
        const isPromo = isIndividualPromo || isGeneralPercentagePromo;
        
        let finalPrice = priceInfo.finalPrice;
        let finalPromoDiscount = 0;
        if (isPromo) {
          finalPromoDiscount = isIndividualPromo ? (selectedSlot.booking?.promoDiscountPercentage || 0) : priceInfo.discount.value;
          if (finalPromoDiscount > 0) {
            finalPrice = Math.max(0, priceInfo.originalPrice * (1 - finalPromoDiscount / 100));
          } else {
            finalPrice = priceInfo.originalPrice;
          }
        }

      const reqData = {
        date: selectedDate,
        playerId: currentUser.uid,
        playerName: currentPlayerProfile?.name || currentUser.name || currentUser.email.split('@')[0],
        playerPhoto: currentPlayerProfile?.photoUrl || currentUser.photoUrl || '',
        playerCategory: currentPlayerProfile?.category || 'Cat -',
        clubId: selectedSedeId,
        clubName: selectedSede?.name || 'Club',
        status: 'pending',
        price: finalPrice,
        notes: notes.trim(),
        createdAt: Date.now(),
        option1: {
          courtName: selectedSlot.court,
          timeSlot: selectedSlot.time
        },
        ...(isPromo ? {
          isPromo: true,
          promoDetails: {
            discount: finalPromoDiscount,
            drink: selectedSlot.booking?.promoPerks?.drink || false,
            balls: selectedSlot.booking?.promoPerks?.balls || false,
            winnerPrize: selectedSlot.booking?.promoPerks?.winnerPrize || false
          }
        } : {})
      };

      // ID único correspondiente a la reserva confirmada de la opción 1
      const bookingId = `${selectedSedeId}_${selectedSlot.court.replace(/\s+/g, '')}_${selectedDate}_${selectedSlot.time.replace(':', '')}`;
      const bookingDocRef = doc(db, getCollectionPath('bookings'), bookingId);

      let docId = '';
      
      const requiresPayment = getRequiredDepositPercentage(schedule?.paymentConfig, selectedDate, selectedSlot?.time || '') > 0;

      // Usar transacción para verificar que el casillero no esté reservado antes de enviar la solicitud
      await runTransaction(db, async (transaction) => {
        const bookingSnap = await transaction.get(bookingDocRef);
        if (bookingSnap.exists()) {
          const bData = bookingSnap.data() as Booking;
          const status = bData ? (bData as any).status : null;
          if (status && status !== 'promo' && status !== 'cancelled' && status !== 'rejected') {
            throw new Error("COLLISION");
          }
          if (status === 'cancelled' || status === 'rejected') {
            const archiveId = `${bookingId}_archived_${Date.now()}`;
            const archiveRef = doc(db, getCollectionPath('bookings'), archiveId);
            transaction.set(archiveRef, { ...bData, id: archiveId, archivedAt: Date.now() });
          }
        }

        if (requiresPayment) {
          // Crear la reserva directa y confirmada pero pendiente de pago
          const bookingData: Booking = {
            id: bookingId,
            clubId: selectedSedeId,
            courtName: selectedSlot.court,
            date: selectedDate,
            timeSlot: selectedSlot.time,
            duration: schedule?.slotDuration || 90,
            status: 'pending',
            isFixed: false,
            notes: notes.trim(),
            price: finalPrice,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            playerId: currentUser.uid,
            playerName: currentPlayerProfile?.name || currentUser.name || currentUser.email.split('@')[0],
            playerCategory: currentPlayerProfile?.category || 'Cat -',
            playerPhoto: currentPlayerProfile?.photoUrl || currentUser.photoUrl || '',
            paymentStatus: 'pending',
            paymentExpiresAt: Date.now() + 5 * 60 * 1000, // 5 minutos para pagar
            confirmationStatus: 'pending_confirmation',
            ...(isPromo ? {
              isPromo: true,
              promoDiscountPercentage: finalPromoDiscount,
              promoPerks: {
                drink: selectedSlot.booking?.promoPerks?.drink || false,
                balls: selectedSlot.booking?.promoPerks?.balls || false,
                winnerPrize: selectedSlot.booking?.promoPerks?.winnerPrize || false
              }
            } : {})
          };
          
          docId = bookingId;
          
          // Eliminar promo si existiera (para limpiar)
          if (bookingSnap.exists() && (bookingSnap.data() as Booking).status === 'promo') {
             transaction.delete(bookingDocRef);
          }
          
          transaction.set(bookingDocRef, bookingData);
        } else {
          // Crear solicitud de reserva si NO requiere seña
          const reqColRef = collection(db, getCollectionPath('bookingRequests'));
          const newReqDocRef = doc(reqColRef);
          docId = newReqDocRef.id;

          transaction.set(newReqDocRef, reqData);
        }

        // Eliminar el bloqueo temporal dentro de la misma transacción
        if (myActiveLockId) {
          const lockDocRef = doc(db, getCollectionPath('slotLocks'), myActiveLockId);
          transaction.delete(lockDocRef);
        }
      });

      // Enviar notificación push al administrador/club
      if (requiresPayment) {
        await notifyClubStaff(selectedSedeId, {
          title: 'Reserva generada (Pendiente de Seña)',
          message: `¡${currentPlayerProfile?.name || currentUser.name || 'Un jugador'} ha reservado un turno para el ${selectedDate.split('-').reverse().join('/')}! Seña pendiente.`,
          type: 'booking_request',
          createdAt: Date.now(),
          url: `/?tab=admin&subtab=turnos&date=${selectedDate}`,
          read: false
        });
      } else {
        await notifyClubStaff(selectedSedeId, {
          title: `Nueva solicitud de reserva de ${currentPlayerProfile?.name || currentUser.name || 'Un jugador'}`,
          message: `¡${currentPlayerProfile?.name || currentUser.name || 'Un jugador'} ha solicitado un turno para el ${selectedDate.split('-').reverse().join('/')}!`,
          type: 'booking_request',
          createdAt: Date.now(),
          url: `/?tab=admin&subtab=turnos&requestId=${docId}`,
          read: false
        });
      }

      // Resetear estados
      setMyActiveLockId(null);
      setSelectedSlot(null);
      setOption2Court('');
      setOption2Time('');
      setNotes('');
      setCheckAdminCourt(false);
      setCheckCancelPolicy(false);
      setShowConfirmPopup(false);

      // Ir a la pestaña de mis turnos para ver el progreso
      setActiveSubTab('mis-turnos');
      
      if (requiresPayment) {
        // Ejecutar inmediatamente el flujo de pago con Mercado Pago
        const success = await handlePayBooking(docId, selectedSedeId);
        if (!success) {
          // Si falló la generación del link de pago, eliminamos la reserva generada
          await deleteDoc(doc(db, getCollectionPath('bookings'), docId));
        }
      } else {
        alert('¡Petición de turno enviada con éxito! El club revisará tu propuesta a la brevedad.');
      }
    } catch (error: any) {
      if (error.message === "COLLISION") {
        alert('⚠️ ¡Colisión de reserva detectada! El casillero que intentas solicitar acaba de ser reservado por otro jugador o administrador hace un momento. Por favor, selecciona otro horario.');
      } else {
        console.error('Error al enviar la petición:', error);
        alert('Error al enviar la petición. Revisa tu conexión: ' + error.message);
      }
    } finally {
      setSendingRequest(false);
    }
  };

  // Helper para buscar nombres de clubes
  const getClubName = (clubId: string) => {
    const club = sedesPublicas.find(s => s.uid === clubId);
    return club ? club.name : 'Sede Oficial';
  };

  const getClubPhoto = (clubId: string) => {
    const club = sedesPublicas.find(s => s.uid === clubId);
    return club?.photoUrl || '';
  };

  const renderClubAvatar = (clubId: string) => {
    const photoUrl = getClubPhoto(clubId);
    const name = getClubName(clubId);
    
    return (
      <div className="absolute top-2.5 right-2.5 z-20 shrink-0 select-none pointer-events-none">
        {photoUrl ? (
          <FadeInImage 
            src={photoUrl} 
            alt={name} 
            className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover border border-white/20 shadow-md bg-[#15171d] ring-2 ring-black/20"
          />
        ) : (
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-white/10 shadow-md bg-[#363a4d] flex items-center justify-center ring-2 ring-black/20">
            <span className="text-[12px] font-black text-white uppercase">{name.charAt(0)}</span>
          </div>
        )}
      </div>
    );
  };

  const renderDateSquare = (dateString: string) => {
    try {
      const parts = dateString.split('-');
      const year = parts[0];
      const month = parts[1];
      const day = parts[2];
      
      const dateObj = new Date(`${year}-${month}-${day}T00:00:00`);
      const monthStr = dateObj.toLocaleDateString('es-ES', { month: 'short' }).replace('.', '').toUpperCase();
      
      return (
        <div className="absolute top-2.5 left-2.5 z-20 select-none pointer-events-none flex flex-col items-center justify-center bg-[#363a4d] w-10 h-10 rounded-lg border border-white/5 shadow-inner shrink-0">
          <span className="text-[7.5px] font-black text-zinc-400 uppercase leading-none mb-0.5">
            {monthStr}
          </span>
          <span className="text-base font-black text-white leading-none tracking-tighter">
            {day}
          </span>
        </div>
      );
    } catch (e) {
      return null;
    }
  };

  // NUEVO: Confirmar asistencia a turno fijo
  const handleConfirmAssistance = async (booking: Booking) => {
    try {
      setLoading(true);

      if (booking.isFixed) {
        // 1. Materializar o actualizar esta instancia específica
        let newBookingId = booking.id;
        const isAlreadyMaterialized = booking.id.includes('_materialized_');
        
        if (!isAlreadyMaterialized) {
          newBookingId = `${booking.id}_materialized_${booking.date}`;
        }
        
        const newBookingRef = doc(db, getCollectionPath('bookings'), newBookingId);
        const { id: _, ...bookingData } = booking;
        
        await setDoc(newBookingRef, {
          ...bookingData,
          id: newBookingId,
          playerConfirmed: true,
          status: 'confirmed',
          confirmationStatus: 'confirmed',
          isFixed: true, // Mantener como fijo para no perder el tag de la serie
          updatedAt: Date.now()
        });

        // 2. Excluir esta fecha de la serie original para evitar duplicidad
        const originalIdWithProjected = booking.id.split('_projected_')[0];
        const docWithProjectedOriginal = myConfirmedBookings.find(b => b.id === originalIdWithProjected);
        const originalId = (docWithProjectedOriginal && docWithProjectedOriginal.isFixedRoot) ? originalIdWithProjected : originalIdWithProjected.split('_materialized_')[0];
        const originalRef = doc(db, getCollectionPath('bookings'), originalId);
        
        // Obtener la serie original actual para actualizar excludedDates de forma segura
        const originalDocSnap = await getDoc(originalRef);
        if (originalDocSnap.exists()) {
          const originalData = originalDocSnap.data();
          const currentExcluded = originalData.excludedDates || [];
          if (!currentExcluded.includes(booking.date)) {
            await updateDoc(originalRef, {
              excludedDates: [...currentExcluded, booking.date],
              updatedAt: Date.now()
            });
          }
        }
      } else {
        // Turno único normal
        const bRef = doc(db, getCollectionPath('bookings'), booking.id);
        await updateDoc(bRef, {
          playerConfirmed: true,
          status: 'confirmed',
          confirmationStatus: 'confirmed',
          updatedAt: Date.now()
        });
      }

      // Registro de Actividad
      await logActivity(
        db,
        getCollectionPath,
        currentUser.uid,
        currentPlayerProfile?.name || currentUser.name || 'Jugador',
        'player',
        `Asistencia confirmada - Cancha: ${booking.courtName}, Fecha: ${booking.date}, Horario: ${booking.timeSlot}`
      );

      // Marcar notificación asociada como leída
      const notifsQuery = query(
        collection(db, getCollectionPath('notifications')),
        where('userId', '==', currentUser.uid),
        where('type', '==', 'booking_confirmation_request')
      );
      const notifsSnap = await getDocs(notifsQuery);
      notifsSnap.docs.forEach(async (docSnap) => {
        const notif = docSnap.data();
        if (notif.read) return;
        const baseIdWithProjected = booking.id.split('_projected_')[0];
          const docWithProjected = myConfirmedBookings.find(b => b.id === baseIdWithProjected);
          const baseId = (docWithProjected && docWithProjected.isFixedRoot) ? baseIdWithProjected : baseIdWithProjected.split('_materialized_')[0];
        if (notif.bookingId === booking.id || notif.bookingId?.includes(baseId)) {
          await updateDoc(doc(db, getCollectionPath('notifications'), docSnap.id), { read: true });
        }
      });

      alert('¡Asistencia confirmada! Gracias por avisar.');
    } catch (err) {
      console.error("Error confirming assistance:", err);
      alert('Error al confirmar asistencia.');
    } finally {
      setLoading(false);
    }
  };

  // NUEVO: No asistiré esta vez (Añadir a excludedDates)
  const handleDeclineAssistance = (booking: Booking) => {
    setTargetFixedTurn(booking);
    setShowDeclineModal(true);
  };

  const confirmDeclineAssistance = async () => {
    if (!targetFixedTurn) return;
    const booking = targetFixedTurn;

    try {
      setLoading(true);
      const isAlreadyMaterialized = booking.id.includes('_materialized_');
      
      // Si es un turno proyectado o la raíz, actualizamos los excludedDates de la raíz
      const originalIdWithProjected = booking.id.split('_projected_')[0];
        const docWithProjectedOriginal = myConfirmedBookings.find(b => b.id === originalIdWithProjected);
        const originalId = (docWithProjectedOriginal && docWithProjectedOriginal.isFixedRoot) ? originalIdWithProjected : originalIdWithProjected.split('_materialized_')[0];
      const originalRef = doc(db, getCollectionPath('bookings'), originalId);
      
      const originalDocSnap = await getDoc(originalRef);
      if (originalDocSnap.exists()) {
        const originalData = originalDocSnap.data();
        const currentExcluded = originalData.excludedDates || [];
        if (!currentExcluded.includes(booking.date)) {
          await updateDoc(originalRef, {
            excludedDates: [...currentExcluded, booking.date],
            updatedAt: Date.now()
          });
        }
      }

      // Si es un documento materializado, también lo cancelamos para que desaparezca
      if (isAlreadyMaterialized) {
        const matRef = doc(db, getCollectionPath('bookings'), booking.id);
        await updateDoc(matRef, {
          status: 'cancelled',
          updatedAt: Date.now()
        });
      }
      
      await cancelAssociatedMatch(booking.id);

      // Registro de Actividad
      await logActivity(
        db,
        getCollectionPath,
        currentUser.uid,
        currentPlayerProfile?.name || currentUser.name || 'Jugador',
        'player',
        `Aviso de inasistencia - Cancha: ${booking.courtName}, Fecha: ${booking.date}, Horario: ${booking.timeSlot}`
      );

      // Notificar al club
      if (booking.clubId) {
        try {
          await notifyClubStaff(booking.clubId, {
            type: 'warning',
            title: 'Inasistencia a Turno Fijo',
            body: `El jugador ${currentPlayerProfile?.name || currentUser.name || 'Un jugador'} ha avisado que NO asistirá a su turno fijo el día ${booking.date} a las ${booking.timeSlot}hs en la cancha ${booking.courtName}. Ese cupo queda liberado para esa fecha.`,
            read: false,
            createdAt: Date.now()
          });
        } catch (err) {
          console.error('Error notificando inasistencia al club', err);
        }
      }

      // Marcar notificación asociada como leída
      const notifsQuery = query(
        collection(db, getCollectionPath('notifications')),
        where('userId', '==', currentUser.uid),
        where('type', '==', 'booking_confirmation_request')
      );
      const notifsSnap = await getDocs(notifsQuery);
      notifsSnap.docs.forEach(async (docSnap) => {
        const notif = docSnap.data();
        if (notif.read) return;
        const baseIdWithProjected = booking.id.split('_projected_')[0];
          const docWithProjected = myConfirmedBookings.find(b => b.id === baseIdWithProjected);
          const baseId = (docWithProjected && docWithProjected.isFixedRoot) ? baseIdWithProjected : baseIdWithProjected.split('_materialized_')[0];
        if (notif.bookingId === booking.id || notif.bookingId?.includes(baseId)) {
          await updateDoc(doc(db, getCollectionPath('notifications'), docSnap.id), { read: true });
        }
      });

      alert('Entendido. Hemos avisado al club que no asistirás esta semana.');
      setShowDeclineModal(false);
      setTargetFixedTurn(null);
    } catch (err) {
      console.error("Error declining assistance:", err);
      alert('Error al procesar tu aviso.');
    } finally {
      setLoading(false);
    }
  };

  // NUEVO: Dar de baja turno fijo
  const handleCancelFixedBooking = (booking: Booking) => {
    setTargetFixedTurn(booking);
    setShowCancelFixedModal(true);
  };

  const confirmCancelFixedBooking = async () => {
    if (!targetFixedTurn) return;
    const booking = targetFixedTurn;

    try {
      setLoading(true);
      const originalIdWithProjected = booking.id.split('_projected_')[0];
        const docWithProjectedOriginal = myConfirmedBookings.find(b => b.id === originalIdWithProjected);
        const originalId = (docWithProjectedOriginal && docWithProjectedOriginal.isFixedRoot) ? originalIdWithProjected : originalIdWithProjected.split('_materialized_')[0];
      const originalRef = doc(db, getCollectionPath('bookings'), originalId);
      await updateDoc(originalRef, {
        isFixed: false,
        status: 'cancelled',
        updatedAt: Date.now()
      });

      // Si también existía un documento materializado para esta fecha, lo cancelamos
      if (booking.id.includes('_materialized_')) {
        const matRef = doc(db, getCollectionPath('bookings'), booking.id);
        await updateDoc(matRef, {
          isFixed: false,
          status: 'cancelled',
          updatedAt: Date.now()
        });
      }

      await cancelAssociatedMatch(booking.id);

      // Registro de Actividad
      await logActivity(
        db,
        getCollectionPath,
        currentUser.uid,
        currentPlayerProfile?.name || currentUser.name || 'Jugador',
        'player',
        `Baja de turno fijo - Cancha: ${booking.courtName}, Horario: ${booking.timeSlot}`
      );

      // Notificar al club
      if (booking.clubId) {
        try {
          await notifyClubStaff(booking.clubId, {
            type: 'warning',
            title: 'Baja de Turno Fijo',
            body: `El jugador ${currentPlayerProfile?.name || currentUser.name || 'Un jugador'} se ha dado de baja definitivamente de su turno fijo de los días ${new Date(booking.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' })} a las ${booking.timeSlot}hs en la cancha ${booking.courtName}.`,
            read: false,
            createdAt: Date.now()
          });
        } catch (err) {
          console.error('Error notificando baja al club', err);
        }
      }

      // Marcar notificación asociada como leída
      const notifsQuery = query(
        collection(db, getCollectionPath('notifications')),
        where('userId', '==', currentUser.uid),
        where('type', '==', 'booking_confirmation_request')
      );
      const notifsSnap = await getDocs(notifsQuery);
      notifsSnap.docs.forEach(async (docSnap) => {
        const notif = docSnap.data();
        if (notif.read) return;
        const baseIdWithProjected = booking.id.split('_projected_')[0];
          const docWithProjected = myConfirmedBookings.find(b => b.id === baseIdWithProjected);
          const baseId = (docWithProjected && docWithProjected.isFixedRoot) ? baseIdWithProjected : baseIdWithProjected.split('_materialized_')[0];
        if (notif.bookingId === booking.id || notif.bookingId?.includes(baseId)) {
          await updateDoc(doc(db, getCollectionPath('notifications'), docSnap.id), { read: true });
        }
      });

      alert('Tu turno fijo ha sido dado de baja. Ahora es una reserva normal para esta fecha.');
      setShowCancelFixedModal(false);
      setTargetFixedTurn(null);
    } catch (err) {
      console.error("Error canceling fixed booking:", err);
      alert('Error al procesar la baja.');
    } finally {
      setLoading(false);
    }
  };

  const renderHistorialCard = (item: any) => {
    return (
      <div key={item.id} className="flex items-center justify-between p-2 sm:p-2.5 bg-[#15171d] border border-[#2d313f] hover:border-[#3e4357] rounded-xl gap-2 sm:gap-3 transition-colors w-full">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <div className="w-8 h-8 rounded-md bg-[#222530] border border-[#2d313f] flex flex-col items-center justify-center shrink-0">
            <span className="text-[8px] font-bold text-zinc-500 uppercase tracking-widest leading-none mb-0.5">{item.date.split('-')[1]}</span>
            <span className="text-[11px] font-black text-white leading-none">{item.date.split('-')[2]}</span>
          </div>
          <div className="min-w-0">
            <h4 className="text-[10px] sm:text-[11px] font-black text-white uppercase tracking-wider truncate">{getClubName(item.clubId || '')}</h4>
            <p className="text-[9px] text-zinc-400 font-bold tracking-wide mt-0.5 truncate">{item.courtName} • {item.timeSlot}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0 justify-end">
          {item.type === 'booking' ? (
            <span className={`text-[7.5px] sm:text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0 ${item.status === 'paid'
              ? 'bg-[#C5FF4A]/20 text-[#C5FF4A] border border-[#C5FF4A]/30'
              : (item.status === 'cancelled' || (item as any).status === 'rejected')
                ? 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
                : 'bg-blue-400/20 text-blue-400 border border-blue-400/30'
              }`}>
              {item.status === 'paid' ? 'PAGADO' : (item.status === 'cancelled' || (item as any).status === 'rejected') ? 'RECHAZADO' : item.paymentStatus === 'pending' ? 'PENDIENTE PAGO' : 'CONFIRMADO'}
            </span>
          ) : (
            <span className={`text-[7.5px] sm:text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0 ${(item as any).status === 'rejected'
              ? 'bg-[#2d313f]/50 text-zinc-400 border border-[#2d313f]'
              : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
              }`}>
              {(item as any).status === 'rejected' ? 'RECHAZADO' : 'PENDIENTE'}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 max-w-7xl mx-auto px-0 pb-2 pt-0 sm:px-4 sm:pb-4 sm:pt-0 w-full overflow-x-hidden">

      <div className="flex flex-col mb-4 gap-4 items-center text-center">
        <div className="flex flex-col items-center w-full px-4 sm:px-0">
          <div className="flex items-center justify-center w-full sm:max-w-4xl gap-1 sm:gap-2 mb-1">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-white/40"></div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-widest text-white flex items-center justify-center gap-3 shrink-0 drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]">
              <img src="/grid-icon.png" alt="Reservas" className="w-7 h-7 sm:w-9 sm:h-9 object-contain opacity-90" style={{ filter: 'brightness(0) invert(1)' }} />
              Reservas
            </h2>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent via-white/10 to-white/40"></div>
          </div>
          <p className="opacity-50 text-[10px] sm:text-xs font-medium max-w-md mx-auto mt-2">Reservá y gestioná tus turnos desde un solo lugar</p>
        </div>
      </div>

      {/* 1. SUB-PESTAÑAS */}
      <div className="flex justify-center w-full pt-1 pb-1 px-2 sm:px-0">
        <div className="inline-flex justify-center bg-black/40 backdrop-blur-md border border-white/10 p-1.5 rounded-xl gap-1.5 shadow-lg relative z-10">
          <button
            onClick={() => setActiveSubTab('grilla')}
            className={`px-6 py-2.5 rounded-lg text-[10px] sm:text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 border ${activeSubTab === 'grilla'
              ? 'theme-primary-bg text-black border-yellow-400 shadow-md scale-100'
              : 'text-zinc-400 hover:text-white hover:bg-[#1a1c23] border-transparent hover:border-[#2d313f]'
              }`}
          >
            <CalendarDays size={15} /> Reservar
          </button>
          <button
            onClick={() => setActiveSubTab('mis-turnos')}
            className={`px-6 py-2.5 rounded-lg text-[10px] sm:text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 relative border ${activeSubTab === 'mis-turnos'
              ? 'theme-primary-bg text-black border-yellow-400 shadow-md scale-100'
              : 'text-zinc-400 hover:text-white hover:bg-[#1a1c23] border-transparent hover:border-[#2d313f]'
              }`}
          >
            <History size={15} /> Mis Turnos
            {totalMyTurnosCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-white text-black text-[9px] font-black w-5 h-5 rounded-full flex items-center justify-center border-2 border-[#15171d] shadow-sm z-20">
                {totalMyTurnosCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* 2. CONTENIDO PRINCIPAL: SELECCIÓN DE SEDE O GRILLA */}
      {activeSubTab === 'grilla' && (
        <div className="space-y-3 animate-in fade-in duration-500">

          {!selectedSedeId ? (
            /* SECTOR DE SELECCIÓN DE SEDE INICIAL (CUANDO NO HAY NINGUNA SELECCIONADA) */
            <div className="flex justify-center w-full">
              <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-5 md:p-6 text-center relative overflow-hidden mt-2 mb-4 inline-flex flex-col items-center min-w-[280px] max-w-3xl">
                {/* Glowing decorative effects */}
                <div className="absolute -top-24 -left-24 w-48 h-48 bg-[var(--primary-color)]/5 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-[var(--primary-color)]/5 rounded-full blur-3xl pointer-events-none" />

                <div className="relative z-10 flex flex-col items-center gap-4 w-full">
                  <div className="space-y-1 flex flex-col items-center justify-center text-center w-full">
                    <h3 className="text-sm md:text-base font-black uppercase tracking-wider text-white/70 theme-font-secondary">
                      Selecciona el Club
                    </h3>
                    <div className="w-full h-px bg-white/10 mt-1" />
                  </div>

                  {sedesPublicas.length <= 5 ? (
                    /* REGLA: HASTA 5 SEDES - SE MUESTRAN LOS LOGOS DIRECTAMENTE */
                    <div className="flex flex-wrap items-center justify-center gap-5 md:gap-6 py-2 w-full">
                      {sedesPublicas.map((s) => (
                        <button
                          key={s.uid}
                          onClick={() => {
                            setSelectedSedeId(s.uid);
                            setSearchSedeQuery(s.name);
                          }}
                          className="group flex flex-col items-center gap-2.5 focus:outline-none focus:scale-105 transition-all duration-300"
                        >
                          {/* Wrapper del Logo con animación premium */}
                          <div className="relative w-16 h-16 md:w-20 md:h-20 bg-transparent flex items-center justify-center transition-all duration-300 shrink-0">
                            {s.photoUrl ? (
                              <FadeInImage
                                src={s.photoUrl}
                                alt={s.name}
                                className="w-full h-full object-contain p-1 rounded-full transition-transform duration-500 group-hover:scale-110"
                              />
                            ) : (
                              <div className="absolute inset-0 bg-[#15171d] rounded-full flex items-center justify-center">
                                <MapPin size={20} className="text-zinc-600 group-hover:text-[var(--primary-color)] transition-colors" />
                              </div>
                            )}
                          </div>

                          <div className="text-center min-w-[90px] max-w-[120px]">
                            <span className="block text-[11px] md:text-xs font-black text-zinc-300 group-hover:text-white tracking-wide uppercase transition-colors truncate">
                              {s.name}
                            </span>
                            <span className="block text-[8px] md:text-[9px] font-bold text-zinc-500 group-hover:text-[var(--primary-color)]/80 transition-colors mt-0.5 truncate max-w-full">
                              {s.address || 'Sede Oficial'}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    /* REGLA: MÁS DE 5 SEDES - SE MUESTRA BARRA DE BÚSQUEDA EXPLICITAMENTE */
                    <div className="w-full max-w-sm relative mt-1">
                      <div className="relative">
                        <input
                          type="text"
                          value={searchSedeQuery}
                          onChange={(e) => {
                            setSearchSedeQuery(e.target.value);
                            setShowSedeDropdown(true);
                          }}
                          onFocus={() => setShowSedeDropdown(true)}
                          placeholder="Escribe el nombre del club..."
                          className="w-full bg-[#15171d] border border-[#2d313f] rounded-xl pl-10 pr-4 py-3 text-xs text-white focus:outline-none focus:border-[var(--primary-color)]/60 transition-all placeholder-zinc-500 font-bold"
                        />
                        <Search size={14} className="absolute left-3.5 top-3.5 text-zinc-500" />
                      </div>

                      {/* Menú Desplegable flotante en el selector inicial */}
                      {showSedeDropdown && (
                        <div className="absolute z-30 left-0 right-0 mt-1 bg-[#222530] border border-[#2d313f] rounded-xl max-h-56 overflow-y-auto shadow-2xl animate-in fade-in duration-200">
                          {filteredSedes.length === 0 ? (
                            <div className="p-3 text-center text-xs text-zinc-400">No se encontraron clubes</div>
                          ) : (
                            filteredSedes.map((s) => (
                              <div
                                key={s.uid}
                                onClick={() => {
                                  setSelectedSedeId(s.uid);
                                  setSearchSedeQuery(s.name);
                                  setShowSedeDropdown(false);
                                }}
                                className="p-2.5 hover:bg-[#2d313f] cursor-pointer flex items-center justify-between border-b border-[#2d313f]/40 last:border-b-0 transition-all"
                              >
                                <div className="flex items-center gap-2.5 min-w-0">
                                  {s.photoUrl ? (
                                    <FadeInImage src={s.photoUrl} alt={s.name} className="w-6 h-6 rounded-full object-contain bg-transparent" />
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-[#1a1c23] flex items-center justify-center">
                                      <MapPin size={10} className="text-zinc-500" />
                                    </div>
                                  )}
                                  <div className="text-left min-w-0">
                                    <span className="block text-[11px] font-black text-white truncate">{s.name}</span>
                                    <span className="block text-[8px] text-zinc-500 font-bold truncate">{s.address || 'Sede Oficial'}</span>
                                  </div>
                                </div>
                                <Check size={10} className="text-[var(--primary-color)] shrink-0" />
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* GRILLA DE RESERVAS COMPLETA (UNA VEZ QUE SE SELECCIONÓ LA SEDE) */
            <div className="space-y-4 animate-in fade-in duration-500">
              {/* CONTROLES DE FILTRO (Sede + Fecha) */}
              <div className="bg-black/20 backdrop-blur-md border-y sm:border border-white/5 rounded-none sm:rounded-2xl px-2 py-4 sm:p-5 flex flex-col lg:flex-row items-center justify-between gap-6 shadow-lg w-full text-center lg:text-left">

                {/* Selector de Sede Compacto */}
                <div className="relative flex-1 flex items-center justify-center w-full">
                  {/* Botón de Volver */}
                  <button
                    onClick={() => {
                      if (window.history.state && window.history.state.modalOpen) {
                        window.history.back();
                      } else {
                        setSelectedSedeId('');
                        setSearchSedeQuery('');
                      }
                    }}
                    className="absolute left-0 z-10 h-10 px-3 rounded-xl border border-white/40 bg-black/40 hover:bg-white/10 hover:border-white/70 text-white text-xs font-black transition-all flex items-center justify-center gap-1.5 hover:scale-[1.02] active:scale-[0.95] shrink-0"
                    title="Volver a la selección de sedes"
                  >
                    <ArrowLeft size={14} className="hidden sm:block" /> Volver
                  </button>

                  {/* Club info card */}
                  <div className="flex items-center justify-center gap-2.5 min-w-0 w-full px-16 sm:px-24">
                    {/* Club profile photo */}
                    {selectedSede?.photoUrl ? (
                      <FadeInImage
                        src={selectedSede.photoUrl}
                        alt={selectedSede.name}
                        className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border border-[#2d313f] shadow-md shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#15171d] border border-[#2d313f] flex items-center justify-center shadow-md shrink-0">
                        <MapPin size={18} className="text-zinc-500" />
                      </div>
                    )}
                    {/* Club name */}
                    <div className="text-left min-w-0">
                      <h3 className="text-lg md:text-xl font-black text-white truncate leading-tight">{selectedSede?.name || 'Club'}</h3>
                    </div>
                  </div>
                </div>

                {/* Selector de Fecha Unificado (Carrusel) */}
              <div className="relative w-full">
                <div 
                  className="w-full shrink-0 flex items-center justify-start overflow-x-auto no-scrollbar py-3 pl-2 pr-16 gap-2"
                  style={{ WebkitMaskImage: 'linear-gradient(to right, black 85%, transparent 100%)', maskImage: 'linear-gradient(to right, black 85%, transparent 100%)' }}
                >
                  {desktopDays.map((day, idx) => {
                    const isSelected = selectedDate === day.dateStr;
                    const [y, m, d] = day.dateStr.split("-");
                    const dateObj = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
                    const dayNames = ["DOM", "LUN", "MAR", "MIE", "JUE", "VIE", "SAB"];
                    
                    const dayName = dayNames[dateObj.getDay()];
                    const dayNum = dateObj.getDate() + "/" + (dateObj.getMonth() + 1);
                    const isToday = idx === 0;

                    return (
                      <button
                        key={day.dateStr}
                        onClick={() => setSelectedDate(day.dateStr)}
                        className={`relative flex flex-col items-center justify-center shrink-0 w-[4rem] h-[4.2rem] sm:w-[4.5rem] sm:h-[4.8rem] rounded-2xl transition-all duration-300 border ${
                          isSelected
                            ? "theme-primary-bg text-black border-transparent shadow-lg scale-[1.03] z-10"
                            : "bg-black/40 text-white/50 border-[#2d313f] hover:bg-white/10 hover:text-white"
                        }`}
                      >
                        {isToday && (
                          <span className={`absolute -top-2 px-2 py-0.5 rounded-full text-[8px] sm:text-[9px] font-black uppercase tracking-widest shadow-md ${isSelected ? "bg-black theme-primary-text" : "theme-primary-bg text-black"}`}>
                            HOY
                          </span>
                        )}
                        <span className={`text-[16px] sm:text-[20px] font-black uppercase tracking-wider leading-none mb-0.5 ${isSelected ? "text-black" : "text-white"}`}>
                          {dayName}
                        </span>
                        <span className={`text-[12px] sm:text-[14px] font-black leading-none mb-0.5 ${isSelected ? "text-black/70" : "text-zinc-400"}`}>
                          {dayNum}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {/* Indicador de scroll (Solo en movil) */}
                <div className="absolute right-1 top-0 bottom-0 pointer-events-none flex items-center justify-end sm:hidden">
                  <div className="bg-white/10 backdrop-blur-sm rounded-full p-1 border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.3)] animate-pulse">
                    <ChevronRight size={22} className="text-white drop-shadow-md" />
                  </div>
                </div>
              </div>
              </div>

              {/* Contenedor de la tabla (sin las leyendas) */}
              <div className="bg-black/20 backdrop-blur-md rounded-none sm:rounded-b-2xl overflow-hidden shadow-xl w-full">
                {/* TABLA HORARIA DE CANCHAS */}
                {loading ? (
                  <div className="p-12 text-center text-zinc-400 text-xs font-bold flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-t-[var(--primary-color)] border-zinc-700 rounded-full animate-spin" />
                    <span>Cargando disponibilidad...</span>
                  </div>
                ) : !schedule ? (
                  <div className="p-12 text-center text-zinc-400 space-y-3">
                    <AlertCircle size={32} className="mx-auto text-zinc-600 animate-bounce" />
                    <p className="text-xs font-black uppercase tracking-wider">Sede sin grilla configurada</p>
                    <p className="text-[10px] text-zinc-500 max-w-sm mx-auto">Esta sede aún no ha completado el setup de horarios de apertura, cierre o duración de turnos. Prueba seleccionando otro club.</p>
                  </div>
                ) : timeSlots.length === 0 ? (
                  <div className="p-12 text-center text-zinc-400 space-y-2">
                    <Clock size={32} className="mx-auto text-zinc-600" />
                    <p className="text-xs font-black uppercase">Sin horarios hábiles para hoy</p>
                    <p className="text-[10px] text-zinc-500">Revisa la configuración horaria del club.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto w-full">
                    <table className="w-full min-w-[340px] md:min-w-[650px] border-collapse table-fixed">
                      <thead>
                        <tr className="border-b border-white/5 bg-black/40">
                          <th className="w-14 md:w-24 p-1 md:p-3 text-center text-[10px] md:text-xs font-black uppercase tracking-wider text-zinc-400 border-r border-white/5 bg-black/60">
                            <span className="hidden md:inline">Horario</span>
                            <span className="md:hidden">H.</span>
                          </th>
                          {schedule.courts.map((court) => (
                            <th key={court} className="p-1 md:p-3 text-center text-[10px] md:text-xs font-black uppercase tracking-widest text-white border-r border-white/5 last:border-r-0 truncate">
                              {court}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {timeSlots.map((time) => (
                          <tr key={time} className="booking-row border-b border-white/5 transition-colors duration-200">
                            {/* Celda del Horario */}
                            <td className="p-1 md:p-3 text-center text-[10px] md:text-xs font-black tracking-wider text-theme-primary border-r border-white/5 flex flex-col md:flex-row items-center justify-center gap-0.5 md:gap-1 h-[54px] md:h-[76px] bg-black/60">
                              <span>{time}</span>
                            </td>

                            {/* Celdas por Cancha */}
                            {schedule.courts.map((court) => {
                              // 1. Buscar si hay una reserva confirmada/pagada para este turno (prioridad sobre la plantilla de promo)
                              const confirmedBooking = bookings.find(
                                (b) => b.clubId === selectedSedeId && b.courtName === court && b.timeSlot === time && b.status !== 'promo' && b.playerName !== 'DESBLOQUEADO'
                              );
                              
                              const exceptionBooking = bookings.find(
                                (b) => b.clubId === selectedSedeId && b.courtName === court && b.timeSlot === time && b.status !== 'promo' && b.playerName === 'DESBLOQUEADO'
                              );

                              // 2. Buscar si hay una oferta/plantilla de promoción activa
                              const promoBooking = bookings.find(
                                (b) => b.clubId === selectedSedeId && b.courtName === court && b.timeSlot === time && b.status === 'promo'
                              );

                              if (confirmedBooking) {
                                if (confirmedBooking.playerName === 'BLOQUEADO') {
                                  return (
                                    <td key={court} className="p-0.5 md:p-1.5 border-r border-white/5 last:border-r-0 cursor-not-allowed bg-black/20">
                                      <div className="h-full min-h-[44px] md:min-h-[64px] border border-zinc-900/60 rounded-lg md:rounded-xl p-1 md:p-2 flex flex-col justify-center items-center bg-black/60 text-zinc-500 shadow-inner">
                                        <span className="text-[8px] md:text-[10px] font-black tracking-widest uppercase text-zinc-600 text-center">INHABILITADA</span>
                                        {confirmedBooking.notes && (
                                          <span className="text-[6px] md:text-[8px] font-bold text-zinc-700 truncate max-w-full text-center mt-0.5" title={confirmedBooking.notes}>
                                            {confirmedBooking.notes}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                  );
                                }

                                const isMine = confirmedBooking.playerId === currentUser.uid;

                                if (isMine) {
                                  return (
                                  <td key={court} className="p-0.5 md:p-1.5 border-r border-white/5 last:border-r-0 bg-black/20"
                                      onClick={async () => {
                                        if (confirmedBooking.paymentStatus === 'pending') {
                                          if(await window.confirm('¿Deseas cancelar el proceso de pago para liberar este turno y volver a reservarlo?')) {
                                            try {
                                              await deleteDoc(doc(db, getCollectionPath('bookings'), confirmedBooking.id));
                                            } catch(e) {}
                                          }
                                        }
                                      }}
                                  >
                                    <div className={`group h-full min-h-[44px] md:min-h-[64px] border ${confirmedBooking.paymentStatus === 'pending' ? 'border-yellow-500/40 bg-yellow-500/[0.05] text-yellow-500 cursor-pointer hover:bg-red-500/10 hover:border-red-500/40' : 'border-[#C5FF4A]/20 bg-[#C5FF4A]/[0.05] text-[#C5FF4A]'} rounded-lg md:rounded-xl p-1 md:p-2 flex flex-col justify-center items-center relative shadow-inner text-center transition-all`}>
                                      <div className="flex flex-col items-center justify-center min-w-0 w-full h-full text-center gap-1">
                                        <span className={`text-[9px] md:text-[11px] font-black tracking-tight truncate w-full block text-center uppercase ${confirmedBooking.paymentStatus === 'pending' ? 'text-yellow-500' : 'text-[#C5FF4A]'}`}>
                                          MI RESERVA
                                        </span>
                                        {confirmedBooking.paymentStatus === 'pending' && (
                                          <span className="text-[7px] md:text-[8px] font-black tracking-wider uppercase px-1.5 py-0.5 rounded truncate max-w-full block text-center bg-yellow-500/20 border border-yellow-500/30 text-yellow-500 animate-pulse">
                                            SEÑA PENDIENTE
                                          </span>
                                        )}
                                        <span className={`flex items-center justify-center gap-1 text-[7px] md:text-[8px] font-black tracking-wider uppercase px-1.5 py-0.5 rounded truncate max-w-full text-center transition-all ${confirmedBooking.paymentStatus === 'pending' ? 'bg-white/10 border border-white/20 text-white group-hover:bg-red-500/20 group-hover:text-red-400 group-hover:border-red-500/30' : 'bg-[#C5FF4A]/20 border border-[#C5FF4A]/30 text-[#C5FF4A]'}`}>
                                          {confirmedBooking.status === 'paid' ? 'PAGADO' : confirmedBooking.paymentStatus === 'pending' ? (
                                            <>
                                              <X size={8} className="stroke-[3]" /> CANCELAR
                                            </>
                                          ) : 'CONFIRMADO'}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  );
                                }

                                // Ocupado (Privacidad Activada)
                                return (
                                  <td key={court} className="p-0.5 md:p-1.5 border-r border-white/5 last:border-r-0 bg-black/20">
                                    <div className="h-full min-h-[44px] md:min-h-[64px] border border-zinc-800 rounded-lg md:rounded-xl p-1 md:p-2 flex flex-col justify-center items-center bg-[#15171d] text-zinc-500 shadow-inner">
                                      <span className="text-[8px] md:text-[10px] font-black tracking-widest uppercase text-zinc-600">RESERVADO</span>
                                    </div>
                                  </td>
                                );
                              }

                              // 3. ¿Esta celda se está bloqueando localmente en este momento?
                              if (lockingSlot && lockingSlot.court === court && lockingSlot.time === time) {
                                return (
                                  <td key={court} className="p-0.5 md:p-1.5 border-r border-white/5 last:border-r-0 bg-black/20">
                                    <div className="h-full min-h-[44px] md:min-h-[64px] border border-[var(--primary-color)]/30 rounded-lg md:rounded-xl p-1 md:p-2 flex flex-col justify-center items-center bg-[var(--primary-color)]/[0.03] text-zinc-400">
                                      <div className="w-4 h-4 border-2 border-t-[var(--primary-color)] border-zinc-700 rounded-full animate-spin" />
                                      <span className="text-[7px] font-black uppercase text-zinc-500 mt-1">Conectando...</span>
                                    </div>
                                  </td>
                                );
                              }

                              // 4. ¿Está bloqueado temporalmente por alguien? (Optimistic Lock)
                              const activeLock = activeLocks.find(
                                (l) => l.courtName === court && l.timeSlot === time && l.expiresAt > Date.now()
                              );

                              if (activeLock) {
                                const isMyLock = activeLock.lockedBy === currentUser.uid;

                                if (!isMyLock) {
                                  return (
                                    <td key={court} className="p-0.5 md:p-1.5 border-r border-white/5 last:border-r-0 bg-black/20">
                                      <div className="h-full min-h-[44px] md:min-h-[64px] border border-zinc-700/50 rounded-lg md:rounded-xl p-1 md:p-2 flex flex-col justify-center items-center bg-[#15171d]/50 text-zinc-500 relative text-center">
                                        <span className="relative flex h-1.5 w-1.5 mb-1.5">
                                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-zinc-400 opacity-75"></span>
                                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-zinc-500"></span>
                                        </span>
                                        <span className="text-[8px] md:text-[9px] font-black tracking-widest uppercase text-zinc-500/90 truncate max-w-full">
                                          RESERVANDO...
                                        </span>
                                        <span className="text-[6px] md:text-[7px] text-zinc-600 font-bold mt-0.5 uppercase tracking-wider truncate max-w-full">
                                          En progreso
                                        </span>
                                      </div>
                                    </td>
                                  );
                                }
                              }

                              // 5. ¿Tiene una solicitud pendiente? (Capa 1)
                              const pendingReq = clubPendingRequests.find(
                                (r) => r.option1?.courtName === court && r.option1?.timeSlot === time
                              );

                              if (pendingReq) {
                                const isMyReq = pendingReq.playerId === currentUser.uid;

                                if (isMyReq) {
                                  return (
                                    <td key={court} className="p-0.5 md:p-1.5 border-r border-white/5 last:border-r-0 bg-black/20">
                                      <div className="h-full min-h-[44px] md:min-h-[64px] border border-amber-500/40 rounded-lg md:rounded-xl p-1 md:p-2 flex flex-col justify-center items-center bg-amber-500/10 text-amber-400 relative shadow-inner text-center animate-pulse">
                                        <div className="flex flex-col items-center justify-center min-w-0 w-full h-full text-center gap-1">
                                          <span className="text-[9px] md:text-[11px] font-black tracking-tight truncate w-full block text-center uppercase text-amber-400">
                                            MI SOLICITUD
                                          </span>
                                          <span className="text-[7px] md:text-[8px] font-black tracking-wider uppercase px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/30 text-amber-400 truncate max-w-full block text-center">
                                            PENDIENTE
                                          </span>
                                        </div>
                                      </div>
                                    </td>
                                  );
                                }

                                // Solicitado por otro jugador
                                return (
                                  <td key={court} className="p-0.5 md:p-1.5 border-r border-white/5 last:border-r-0 bg-black/20">
                                    <div className="h-full min-h-[44px] md:min-h-[64px] border border-zinc-800 rounded-lg md:rounded-xl p-1 md:p-2 flex flex-col justify-center items-center bg-[#15171d] text-zinc-500 shadow-inner">
                                        <span className="text-[8px] md:text-[10px] font-black tracking-widest uppercase text-zinc-600">PENDIENTE</span>
                                        <span className="text-[7px] md:text-[8px] font-bold text-zinc-700 mt-0.5">Solicitado</span>
                                      </div>
                                  </td>
                                );
                              }

                              // ¿Esta celda está bloqueada por un Día Off?
                              const blockedInfo = checkIfSlotBlockedByOffDay(
                                schedule.offDays,
                                selectedDate,
                                court,
                                time,
                                new Date(selectedDate + 'T00:00:00').getDay()
                              );

                              if (exceptionBooking) {
                                blockedInfo.isBlocked = false;
                              }

                              if (blockedInfo.isBlocked) {
                                return (
                                  <td key={court} className="p-0.5 md:p-1.5 border-r border-white/5 last:border-r-0 cursor-not-allowed bg-black/20">
                                    <div className="h-full min-h-[44px] md:min-h-[64px] border border-zinc-900/60 rounded-lg md:rounded-xl p-1 md:p-2 flex flex-col justify-center items-center bg-black/60 text-zinc-500 shadow-inner">
                                      <span className="text-[8px] md:text-[10px] font-black tracking-widest uppercase text-zinc-600 text-center">INHABILITADA</span>
                                      {blockedInfo.reason && (
                                        <span className="text-[6px] md:text-[8px] font-bold text-zinc-700 truncate max-w-full text-center mt-0.5" title={blockedInfo.reason}>
                                          {blockedInfo.reason}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                );
                              }

                              // 6. Si existe una promoción activa libre para este turno, se renderiza la tarjeta de PROMO
                              if (promoBooking) {
                                const hasDrink = promoBooking.promoPerks?.drink;
                                const hasBalls = promoBooking.promoPerks?.balls;
                                const hasWinnerPrize = promoBooking.promoPerks?.winnerPrize;
                                const discount = promoBooking.promoDiscountPercentage || 0;

                                return (
                                  <td
                                    key={court}
                                    onClick={() => handleSlotClick(court, time, promoBooking)}
                                    className="p-0.5 md:p-1.5 border-r border-white/5 last:border-r-0 cursor-pointer bg-black/20"
                                  >
                                    <div className="h-full min-h-[44px] md:min-h-[64px] border border-dashed border-purple-500/30 bg-purple-500/5 hover:bg-purple-500/10 hover:border-purple-400 hover:scale-[1.02] rounded-lg md:rounded-xl p-1 md:p-1.5 flex flex-col transition-all duration-200 shadow-sm relative overflow-hidden select-none">
                                      {/* Discount tag - absolute top right */}
                                      {discount > 0 && (
                                        <span className="absolute top-0.5 right-0.5 text-[7px] md:text-[8px] font-black text-green-400 px-1 py-0.5 bg-green-500/10 border border-green-500/20 rounded leading-none">
                                          {discount}%
                                        </span>
                                      )}

                                      {/* PROMO centered at top */}
                                      <div className="flex-1 flex flex-col items-center justify-center w-full gap-1">
                                          <span className="text-[9px] md:text-[11px] font-black uppercase tracking-widest bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent drop-shadow-[0_0_6px_rgba(168,85,247,0.6)]">
                                            PROMO
                                          </span>
                                          {(hasDrink || hasBalls || hasWinnerPrize) && (
                                            <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                              {hasDrink && <span className="text-[9px] md:text-[11px]" title="Bebida gratis">🥤</span>}
                                        {hasBalls && <span className="text-[9px] md:text-[11px] flex items-center justify-center drop-shadow-md" title="Pelotas nuevas"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#ccff00" stroke="#9acd00" strokeWidth="1"/><path d="M6 5.3a9 9 0 0 1 0 13.4" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/><path d="M18 5.3a9 9 0 0 0 0 13.4" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/></svg></span>}
                                        {hasWinnerPrize && <span className="text-[9px] md:text-[11px]" title="Premio ganador">🏆</span>}
                                            </div>
                                          )}
                                        </div>
                                    </div>
                                  </td>
                                );
                              }

                              // 7. Celda Libre común (con o sin descuento normal)
                              const priceInfo = getSlotPriceAndDiscount(court, time, selectedDate);
                              const hasDiscount = priceInfo.discount !== null;

                              return (
                                <td
                                  key={court}
                                  onClick={() => handleSlotClick(court, time)}
                                  className="p-0.5 md:p-1.5 border-r border-white/5 last:border-r-0 cursor-pointer bg-black/20 group"
                                >
                                  <div className={`relative h-full min-h-[44px] md:min-h-[64px] border border-dashed rounded-lg md:rounded-xl flex flex-col items-center justify-center transition-all duration-300 p-1 ${
                                    hasDiscount
                                      ? 'border-purple-500/30 bg-purple-500/5 group-hover:bg-purple-500/10 group-hover:border-purple-400 group-hover:scale-[1.02] text-purple-400 group-hover:text-purple-300'
                                      : 'border-zinc-500/60 group-hover:border-solid group-hover:border-white group-hover:scale-[1.05] text-zinc-500 group-hover:text-white'
                                  }`}>
                                    {hasDiscount && (
                                      <span className="absolute top-0.5 right-0.5 text-[8px] md:text-[9px] font-black uppercase text-emerald-400 tracking-wider bg-emerald-500/10 px-1 py-0.5 rounded border border-emerald-500/15 leading-none">
                                        {priceInfo.discount?.discountType === 'percentage'
                                          ? `${priceInfo.discount.value}%`
                                          : `-$${priceInfo.discount?.value}`
                                        }
                                      </span>
                                    )}
                                    {getRequiredDepositPercentage(schedule?.paymentConfig, selectedDate, time) > 0 && (
                                      <span className="absolute bottom-0.5 right-0.5 text-[6.5px] md:text-[7.5px] font-black uppercase text-sky-400 tracking-wider bg-sky-500/10 px-1 py-0.5 rounded border border-sky-500/15 leading-none shadow-[0_0_10px_rgba(14,165,233,0.1)]">
                                        Seña: {getRequiredDepositPercentage(schedule?.paymentConfig, selectedDate, time)}%
                                      </span>
                                    )}
                                    {hasDiscount ? (
                                        <span className="text-[9px] md:text-[11px] font-black uppercase tracking-widest bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent drop-shadow-[0_0_6px_rgba(168,85,247,0.6)]">
                                          PROMO
                                        </span>
                                      ) : (
                                        <Plus size={14} className="md:w-[18px] md:h-[18px]" />
                                      )}
                                    </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3. CONTENIDO: PESTAÑA HISTORIAL "MIS TURNOS CONFIRMADOS" */}
      {activeSubTab === 'mis-turnos' && (
        <div className="space-y-4 max-w-5xl mx-auto w-full">
          <div className="bg-black/40 backdrop-blur-md border-y sm:border border-white/10 rounded-none sm:rounded-2xl px-3 py-5 sm:p-5 shadow-lg w-full">
            {combinedTurnos.length === 0 ? (
              <div className="text-center py-16 text-zinc-500 space-y-3">
                <Calendar size={36} className="mx-auto text-zinc-700 animate-pulse" />
                <p className="text-xs font-black uppercase tracking-wider text-center">Aún no tienes turnos registrados</p>
                <p className="text-[10px] text-zinc-500 max-w-sm mx-auto text-center">Tus turnos confirmados, pagados y propuestas pendientes se listarán aquí en tiempo real con todos sus detalles de estado.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {(() => {
                  const turnosActivos = combinedTurnos.filter(item => {
                    if (item.type === 'booking') {
                      if (item.isFixed && item.playerConfirmed === false) return false;
                      return item.status !== 'paid' && item.status !== 'cancelled';
                    }
                    return item.type === 'request' && item.status !== 'rejected';
                  });
                  const turnosHistorial = combinedTurnos.filter(item => (item.type === 'booking' && (item.status === 'paid' || item.status === 'cancelled')) || (item.type === 'request' && (item as any).status === 'rejected'));

                  const renderTurnoCard = (item: any) => {
                    const isPast = item.date < getLocalDateStr();
                    const activeMatch = myPublicMatches.find(m => m.reservationId === item.id);
                    if (item.type === 'booking') {
                      const isPending = ((item.isFixed || item.confirmationRequested) && item.playerConfirmed === false) || item.status === 'pending' || item.paymentStatus === 'pending';
                      const isYellowStyle = isPending || item.isFixed;
                      return (
                        <div
                          key={`booking-${item.id}`}
                          className={`bg-[#222530] border rounded-xl px-2 py-3 sm:p-4 space-y-2 transition-all hover:scale-[1.01] w-full max-w-md mx-auto flex flex-col h-full transform-gpu backface-visibility-hidden relative ${(isPast || item.status === 'cancelled') 
                            ? 'border-[#2d313f] opacity-60' 
                            : isYellowStyle 
                              ? 'border-yellow-500/30 shadow-[0_0_15px_rgba(234,179,8,0.05)] hover:border-yellow-400/60 hover:shadow-[0_0_20px_rgba(234,179,8,0.12)]'
                              : 'border-blue-500/30 shadow-[0_0_15px_rgba(59,130,246,0.05)] hover:border-blue-400/60 hover:shadow-[0_0_20px_rgba(59,130,246,0.12)]'
                            }`}
                        >
                          {renderDateSquare(item.date)}
                          {renderClubAvatar(item.clubId || '')}
                          {/* Header de la Tarjeta */}
                          <div className="flex flex-col items-center justify-center border-b border-[#2d313f] pb-2 gap-1.5 text-center shrink-0">
                            <div className="min-w-0 w-full text-center px-10 sm:px-12">
                              <h4 className="text-xs font-black text-white truncate uppercase tracking-wider">{getClubName(item.clubId || '')}</h4>
                            </div>
                            <div className="flex items-center gap-1 flex-wrap justify-center">
                              {item.assignedByClub && (
                                <span className="text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-wider shrink-0 bg-white text-black border border-white">
                                  Asignado por el club
                                </span>
                              )}
                              {item.isFixed && (
                                <span className="text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-wider shrink-0 bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center gap-1">
                                  <Sparkles size={9} /> Turno Fijo
                                </span>
                              )}
                              <span className={`text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-wider shrink-0 ${
                                (item.status === 'paid' || item.paymentStatus === 'full_paid')
                                  ? 'bg-green-400/20 text-green-400 border border-green-400/30'
                                  : item.paymentStatus === 'partial_paid'
                                    ? 'bg-emerald-400/20 text-emerald-400 border border-emerald-400/30'
                                    : item.paymentStatus === 'pending'
                                      ? 'bg-orange-400/20 text-orange-400 border border-orange-400/30'
                                      : (item.status === 'cancelled' || (item as any).status === 'rejected')
                                        ? 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
                                        : ((item.isFixed || item.confirmationRequested) && item.playerConfirmed === false)
                                          ? 'bg-yellow-400/20 text-yellow-400 border border-yellow-400/30'
                                          : 'bg-blue-400/20 text-blue-400 border border-blue-400/30'
                              }`}>
                                {(item.status === 'paid' || item.paymentStatus === 'full_paid')
                                  ? `PAGADO (${item.courtPaidPlayersCount || 4}/4)`
                                  : item.paymentStatus === 'partial_paid'
                                    ? `SEÑA PAGADA (${item.courtPaidPlayersCount || 1}/4)`
                                    : item.paymentStatus === 'pending'
                                      ? 'PENDIENTE PAGO'
                                      : (item.status === 'cancelled' || (item as any).status === 'rejected')
                                        ? 'RECHAZADO'
                                        : ((item.isFixed || item.confirmationRequested) && item.playerConfirmed === false)
                                          ? 'PENDIENTE'
                                          : 'CONFIRMADO'}
                              </span>
                                {(item.promoDiscountPercentage > 0 || item.promoPerks?.drink || item.promoPerks?.balls || item.promoPerks?.winnerPrize) && (
                                  <div className="flex items-center gap-1 mt-1 justify-center sm:justify-start w-full sm:w-auto">
                                    {item.promoDiscountPercentage > 0 && (
                                      <span className="text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0 bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center gap-1">
                                        PROMO {item.promoDiscountPercentage}%
                                      </span>
                                    )}
                                    {(item.promoPerks?.drink || item.promoPerks?.balls || item.promoPerks?.winnerPrize) && (
                                      <div className="flex items-center gap-1">
                                        {item.promoPerks.drink && <span className="text-[10px]" title="Bebida de cortesía">🥤</span>}
                                        {item.promoPerks.balls && <span className="text-[10px] flex items-center justify-center drop-shadow-md" title="Pelotas nuevas"><svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#ccff00" stroke="#9acd00" strokeWidth="1"/><path d="M6 5.3a9 9 0 0 1 0 13.4" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/><path d="M18 5.3a9 9 0 0 0 0 13.4" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/></svg></span>}
                                        {item.promoPerks.winnerPrize && <span className="text-[10px]" title="Premio al ganador">🏆</span>}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Detalles del Turno */}
                          <div className="grid grid-cols-3 gap-1.5 text-[10.5px] bg-[#1a1c23] p-2 sm:p-2.5 rounded-lg border border-[#2d313f] text-center shrink-0">
                            <div className="flex flex-col items-center justify-center">
                              <span className="text-[8px] text-zinc-500 uppercase block font-bold tracking-wider">Fecha</span>
                              <span className="text-zinc-200 font-bold mt-0.5 block">{item.date.split('-').reverse().join('/')}</span>
                            </div>
                            <div className="flex flex-col items-center justify-center border-x border-white/5 px-1">
                              <span className="text-[8px] text-zinc-500 uppercase block font-bold tracking-wider">Horario</span>
                              <span className="text-[var(--primary-color)] font-black mt-0.5 block">{item.timeSlot}</span>
                            </div>
                            <div className="flex flex-col items-center justify-center">
                              <span className="text-[8px] text-zinc-500 uppercase block font-bold tracking-wider">Cancha</span>
                              <span className="text-zinc-200 font-black mt-0.5 block truncate w-full" title={item.courtName}>{item.courtName}</span>
                            </div>
                          </div>

                          <div className="flex-1 flex flex-col justify-end">
                            {item.adminNote && (
                              <div className="bg-[#15171d] border border-[#2d313f] p-2 rounded-lg flex gap-2 text-left items-start w-full mb-2">
                                <MessageSquare size={13} className="text-zinc-400 mt-0.5 shrink-0" />
                                <div className="space-y-0.5 min-w-0">
                                  <span className="text-[8px] font-black uppercase tracking-wider text-zinc-450 block">Mensaje del Club</span>
                                  <p className="text-[9.5px] text-white italic leading-relaxed">
                                    "{item.adminNote}"
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Botón de Ayuda o Soporte */}
                            {!isPast && (
                              <div className="flex flex-col gap-2 w-full mt-auto">
                                {item.paymentStatus === 'pending' && (
                                  <div className="flex gap-2 w-full mb-2">
                                    <button
                                      onClick={() => handlePayBooking(item.id, item.clubId || '')}
                                      disabled={payingBookingId !== null}
                                      className="flex-1 bg-[#009EE3] hover:bg-[#0087c2] disabled:opacity-50 text-white text-[10.5px] font-black py-2.5 rounded-lg transition-all duration-300 flex items-center justify-center gap-1.5 border border-transparent hover:scale-[1.02] active:scale-95 shadow-lg shadow-sky-500/10 cursor-pointer"
                                    >
                                      {payingBookingId === item.id ? (
                                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                      ) : (
                                        <>
                                          <div className="w-[18px] h-[18px] bg-white rounded-full flex items-center justify-center shrink-0 overflow-hidden shadow-sm">
                                            <img src="/mp-logo.png" alt="Mercado Pago" className="w-[13px] h-[13px] object-contain" />
                                          </div>
                                          <span>PAGAR CON MERCADO PAGO</span>
                                        </>
                                      )}
                                    </button>
                                    <button
                                      onClick={async () => {
                                        if(await window.confirm('¿Deseas cancelar el proceso de pago para liberar este turno?')) {
                                          try {
                                            await deleteDoc(doc(db, getCollectionPath('bookings'), item.id));
                                          } catch(e) {}
                                        }
                                      }}
                                      className="w-[80px] bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 text-[9px] font-black rounded-lg transition-all flex items-center justify-center flex-col uppercase tracking-wider hover:scale-[1.02]"
                                    >
                                      <span>Cancelar</span>
                                    </button>
                                  </div>
                                )}

                                <div className="flex gap-2 justify-center w-full">
                                  <button
                                    onClick={() => onOpenChatWithClub?.(item.clubId || '')}
                                    className="flex-[1] bg-[#2d313f] hover:bg-[#343847] text-zinc-200 text-[10px] font-black py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-1.5 border border-[#3e4357] hover:border-white hover:scale-[1.02] group/chat"
                                  >
                                    <MessageSquare size={11} className="text-theme-primary group-hover/chat:scale-110 transition-all" />
                                    Consultar al Club
                                  </button>
                                  {(item.status === 'confirmed' || item.status === 'paid') && (
                                    <CancelConfirmedBookingButton item={item} db={db} getCollectionPath={getCollectionPath} />
                                  )}
                                  {(item.status === 'confirmed' || item.status === 'paid') ? (
                                    activeMatch ? (
                                      (activeMatch.status === 'finished' || activeMatch.status === 'completed') ? (
                                        <button
                                          disabled
                                          className="w-full bg-[#1a1c23] text-zinc-500 text-[10px] font-black py-2 rounded-lg flex items-center justify-center gap-1.5 border border-[#2d313f] cursor-not-allowed opacity-80"
                                        >
                                          <Check size={11} />
                                          Finalizado
                                        </button>
                                      ) : (
                                        <button
                                          onClick={() => setMatchToDelete(activeMatch)}
                                          className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-400 text-[10px] font-black py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-1.5 border border-red-500/30 hover:scale-[1.02] shadow-[0_0_10px_rgba(239,68,68,0.1)] cursor-pointer"
                                        >
                                          <Users size={11} />
                                          Quitar Publicacion
                                        </button>
                                      )
                                    ) : null
                                  ) : null}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    } else {
                      // Es una solicitud (pending o rejected)
                      return (
                        <div
                          key={`request-${item.id}`}
                          className={`bg-[#222530] border rounded-xl px-2 py-3 sm:p-4 space-y-2 transition-all w-full max-w-md mx-auto flex flex-col h-full relative ${item.status === 'deposit_required' ? 'border-sky-500/30 shadow-[0_0_15px_rgba(14,165,233,0.05)] hover:border-sky-400/60' : (item as any).status === 'rejected' ? 'border-[#2d313f] opacity-70' : 'border-yellow-500/30 shadow-[0_0_15px_rgba(234,179,8,0.05)] hover:border-yellow-400/60'
                            }`}
                        >
                          {renderDateSquare(item.date)}
                          {renderClubAvatar(item.clubId || '')}
                          {/* Header de la Petición */}
                          <div className="flex flex-col items-center justify-center border-b border-[#2d313f] pb-2 gap-1.5 text-center shrink-0">
                            <div className="min-w-0 w-full text-center px-10 sm:px-12">
                              <h4 className="text-xs font-black text-white truncate uppercase tracking-wider">{getClubName(item.clubId)}</h4>
                            </div>
                            <span className={`text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-wider ${
                              item.status === 'deposit_required' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 animate-pulse' :
                              (item as any).status === 'rejected' ? 'bg-[#2d313f]/50 text-zinc-400 border border-[#2d313f]' :
                              'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 animate-pulse'
                            }`}>
                              {item.status === 'deposit_required' ? 'PENDIENTE - SEÑA REQUERIDA' : (item as any).status === 'rejected' ? 'RECHAZADO' : 'PENDIENTE'}
                            </span>
                              {(item.promoDetails?.discount > 0 || item.promoDetails?.drink || item.promoDetails?.balls || item.promoDetails?.winnerPrize) && (
                                <div className="flex items-center gap-1 mt-1 justify-center sm:justify-start w-full sm:w-auto">
                                  {item.promoDetails.discount > 0 && (
                                    <span className="text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0 bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center gap-1">
                                      PROMO {item.promoDetails.discount}%
                                    </span>
                                  )}
                                  {(item.promoDetails?.drink || item.promoDetails?.balls || item.promoDetails?.winnerPrize) && (
                                    <div className="flex items-center gap-1">
                                      {item.promoDetails.drink && <span className="text-[10px]" title="Bebida de cortesía">🥤</span>}
                                      {item.promoDetails.balls && <span className="text-[10px] flex items-center justify-center drop-shadow-md" title="Pelotas nuevas"><svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#ccff00" stroke="#9acd00" strokeWidth="1"/><path d="M6 5.3a9 9 0 0 1 0 13.4" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/><path d="M18 5.3a9 9 0 0 0 0 13.4" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/></svg></span>}
                                      {item.promoDetails.winnerPrize && <span className="text-[10px]" title="Premio al ganador">🏆</span>}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Detalles del Turno */}
                          <div className="grid grid-cols-3 gap-1.5 text-[10.5px] bg-[#1a1c23] p-2 sm:p-2.5 rounded-lg border border-[#2d313f] text-center shrink-0">
                            <div className="flex flex-col items-center justify-center">
                              <span className="text-[8px] text-zinc-500 uppercase block font-bold tracking-wider">Fecha</span>
                              <span className="text-zinc-200 font-bold mt-0.5 block">{item.date.split('-').reverse().join('/')}</span>
                            </div>
                            <div className="flex flex-col items-center justify-center border-x border-white/5 px-1">
                              <span className="text-[8px] text-zinc-500 uppercase block font-bold tracking-wider">Horario</span>
                              {item.option1 && (
                                <span className="text-[var(--primary-color)] font-black mt-0.5 block">{item.option1.timeSlot}</span>
                              )}
                            </div>
                            <div className="flex flex-col items-center justify-center">
                              <span className="text-[8px] text-zinc-500 uppercase block font-bold tracking-wider">Cancha</span>
                              {item.option1 && (
                                <span className="text-zinc-200 font-black mt-0.5 block truncate w-full" title={item.option1.courtName}>{item.option1.courtName}</span>
                              )}
                            </div>
                          </div>

                          <div className="flex-1 flex flex-col justify-end gap-2 mt-1">
                            {item.notes && (
                              <div className="text-[9.5px] text-zinc-400 bg-[#15171d] p-2 rounded border border-[#2d313f] italic text-center">
                                <span className="text-[8px] font-black uppercase text-zinc-500 block not-italic mb-0.5">Mi nota:</span>
                                "{item.notes}"
                              </div>
                            )}

                            {item.adminNote && (
                              <div className="bg-[#15171d] border border-[#2d313f] p-2 rounded-lg flex gap-2 text-left items-start w-full">
                                <MessageSquare size={13} className="text-zinc-400 mt-0.5 shrink-0" />
                                <div className="space-y-0.5 min-w-0">
                                  <span className="text-[8px] font-black uppercase tracking-wider text-zinc-450 block">Mensaje del Club</span>
                                  <p className="text-[9.5px] text-white italic leading-relaxed">
                                    "{item.adminNote}"
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Estado del Club */}
                            <div className="flex items-center justify-center gap-1.5 text-[9.5px] text-zinc-400 bg-black/10 py-1.5 px-2.5 rounded-lg border border-white/5">
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${(item as any).status === 'rejected' ? 'bg-zinc-500' : 'bg-yellow-500 animate-pulse'}`} />
                              <span className="font-medium text-[9px] leading-tight">
                                {item.status === 'pending' && 'Evaluando tu solicitud.'}
                                {(item as any).status === 'rejected' && 'Rechazada o sin cupos.'}
                                {item.status === 'deposit_required' && (
                                  <div className="flex flex-col items-center gap-1 mt-1">
                                    <span>Se requiere pagar una seña ({(item as any).requiredDepositPercentage}%)</span>
                                    {(item as any).depositExpiresAt && (
                                      <span className="text-red-400 font-bold text-[10px] animate-pulse">
                                        Expira en: {Math.max(0, Math.ceil(((item as any).depositExpiresAt - now) / 60000))} min
                                      </span>
                                    )}
                                  </div>
                                )}
                              </span>
                            </div>

                            {/* Botones de Acción */}
                            {item.status === 'deposit_required' ? (
                              <div className="flex gap-2 justify-center mt-auto w-full">
                                <button
                                  onClick={() => handlePayRequestDeposit(item)}
                                  disabled={payingBookingId === item.id}
                                  className="flex-[2] bg-[#009EE3] hover:bg-[#0087c2] disabled:opacity-50 text-white text-[10px] font-black py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-1.5 border border-[#009EE3] shadow-[0_0_10px_rgba(0,158,227,0.2)] hover:scale-[1.02] active:scale-95 cursor-pointer"
                                >
                                  {payingBookingId === item.id ? (
                                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <>
                                      <div className="w-[18px] h-[18px] bg-white rounded-full flex items-center justify-center shrink-0 overflow-hidden shadow-sm">
                                        <img src="/mp-logo.png" alt="Mercado Pago" className="w-[13px] h-[13px] object-contain" />
                                      </div>
                                      Pagar Seña ({(item as any).requiredDepositPercentage || 25}%)
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={async () => {
                                    if(await window.confirm('¿Seguro que deseas cancelar esta solicitud?')) {
                                      const { deleteDoc, doc } = await import('firebase/firestore');
                                      await deleteDoc(doc(db, getCollectionPath('bookingRequests'), item.id));
                                    }
                                  }}
                                  className="flex-[1] bg-red-500/10 hover:bg-red-500/20 text-red-400 text-[10px] font-black py-2 rounded-lg transition-all duration-300 flex items-center justify-center border border-red-500/30 hover:scale-[1.02]"
                                >
                                  Cancelar
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-2 justify-center mt-auto w-full">
                                <button
                                  onClick={() => onOpenChatWithClub?.(item.clubId || '')}
                                  className="flex-[1] bg-[#2d313f] hover:bg-[#343847] text-zinc-200 text-[10px] font-black py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-1.5 border border-[#3e4357] hover:border-white hover:scale-[1.02] group/chat"
                                >
                                  <MessageSquare size={11} className="text-theme-primary group-hover/chat:scale-110 transition-all" />
                                  Contactar al Club
                                </button>
                                {item.status === 'pending' && <CancelPendingBookingButton item={item} db={db} getCollectionPath={getCollectionPath} />}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }
                  };

                  return (
                    <div className="space-y-10">
                      {/* NUEVA SECCIÓN: TURNOS ASEGURADOS (FIJOS) */}
                      {nextFixedTurns.some(b => !b.playerConfirmed) && (
                        <div className="space-y-4 mb-4">
                          <div className="flex items-center justify-between px-1">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-xl bg-yellow-500/20 flex items-center justify-center border border-yellow-500/30 shadow-[0_0_15px_rgba(234,179,8,0.15)] group-hover:border-yellow-500/50 transition-all">
                                <Pin size={16} className="text-yellow-400 animate-pulse transform-gpu -rotate-12 group-hover:rotate-0 transition-transform duration-300" />
                              </div>
                              <div className="text-left">
                                <h3 className="text-xs font-black uppercase tracking-[0.15em] text-white">Mis Turnos Fijos</h3>
                                <p className="text-[9px] text-zinc-500 font-bold uppercase tracking-wider mt-0.5">Mantén tu compromiso semanal</p>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col gap-5 sm:gap-6 w-full px-1.5 sm:px-2 mt-2">
                            {nextFixedTurns.filter(b => !b.playerConfirmed).map(item => {
                              const activeMatch = myPublicMatches.find(m => m.reservationId === item.id);
                              return (
                                <div
                                  key={`fixed-pending-${item.id}`}
                                  className="w-full bg-[#2d313f] rounded-xl sm:rounded-2xl pt-3.5 pb-2.5 px-2 sm:p-3 flex flex-col md:flex-row items-center gap-2 sm:gap-8 border border-white/30 shadow-[0_0_15px_rgba(255,255,255,0.05)] relative transform-gpu backface-visibility-hidden group hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.12)] transition-all duration-300 md:pl-14"
                                >
                                  {/* Título de Próximo Turno Centrado */}
                                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-20 bg-white text-black text-[8px] font-black uppercase tracking-[0.25em] px-2.5 py-0.5 rounded-full border border-[#2d313f] shadow-[0_2px_8px_rgba(255,255,255,0.15)] whitespace-nowrap">
                                    Próximo Turno
                                  </div>
                                  {renderDateSquare(item.date)}
                                  {renderClubAvatar(item.clubId || '')}


                                  {/* SECCIÓN 1: FECHA Y HORA */}
                                  <div className="flex flex-col items-center justify-center shrink-0 relative z-10 w-full md:w-auto px-1 md:px-0">
                                    <div className="flex flex-col items-center text-center">
                                      <span className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.1em] leading-none mb-1">
                                        {new Date(item.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' }).toUpperCase()}
                                      </span>
                                      <span className="text-xl font-black text-yellow-400 leading-none tracking-tight">{item.timeSlot}</span>
                                    </div>
                                  </div>

                                  {/* SECCIÓN 2: INFO DEL CLUB */}
                                  <div className="flex-1 text-center md:text-left min-w-0 md:pl-6 border-y md:border-y-0 md:border-l border-white/5 py-2 md:py-0 w-full relative z-10">
                                    <div className="flex flex-row items-center justify-center md:justify-start gap-2 flex-wrap w-full">
                                      <h4 className="text-sm sm:text-base font-black text-white uppercase tracking-tight truncate leading-none">
                                        {getClubName(item.clubId || '')}
                                      </h4>
                                      <span className="w-1 h-1 rounded-full bg-zinc-650 shrink-0" />
                                      <div className="flex items-center gap-1 shrink-0">
                                        <MapPin size={9} className="text-zinc-500" />
                                        <span className="text-[10px] font-black text-zinc-400 uppercase tracking-wide leading-none">{item.courtName}</span>
                                      </div>
                                      <span className="w-1 h-1 rounded-full bg-zinc-650 shrink-0" />
                                      <button
                                        onClick={() => onOpenChatWithClub?.(item.clubId || '')}
                                        className="flex items-center gap-1 px-1.5 py-0.5 bg-[#363a4d] hover:bg-theme-primary/20 text-zinc-300 hover:text-theme-primary rounded-md border border-white/5 transition-all group/chat shrink-0 shadow-sm"
                                        title="Chat con el club"
                                      >
                                        <MessageSquare size={9} className="group-hover/chat:scale-110 transition-transform" />
                                        <span className="text-[7.5px] font-black uppercase tracking-tight">Chat</span>
                                      </button>
                                    </div>
                                  </div>

                                  {/* SECCIÓN 3: ACCIONES */}
                                  <div className="flex flex-col sm:flex-row items-center gap-2.5 w-full md:w-auto shrink-0 pt-1.5 md:pt-0 relative z-10 md:pr-10">
                                    {item.confirmationRequested && (
                                      <div className="w-full sm:w-auto flex-1 md:flex-none">
                                        <button
                                          onClick={() => handleConfirmAssistance(item)}
                                          className="w-full px-6 bg-yellow-400 hover:bg-yellow-500 text-black font-black uppercase text-[10px] py-3 rounded-2xl transition-all shadow-lg shadow-yellow-400/10 flex items-center justify-center gap-2 group/btn active:scale-95 whitespace-nowrap"
                                        >
                                          <CheckCircle2 size={14} /> Confirmar Asistencia
                                        </button>
                                      </div>
                                    )}

                                    <div className="flex flex-row sm:flex-col gap-1.5 w-full sm:w-auto">
                                      {item.playerConfirmed ? (
                                        activeMatch ? (
                                          (activeMatch.status === 'finished' || activeMatch.status === 'completed') ? (
                                            <button
                                              disabled
                                              className="flex-1 sm:flex-none flex items-center justify-center px-2.5 py-1.5 bg-[#1a1c23] text-zinc-500 border border-[#2d313f] rounded-lg sm:min-w-[90px] cursor-not-allowed opacity-80"
                                            >
                                              <span className="text-[9px] font-black uppercase tracking-wider flex items-center gap-1"><Check size={10} /> Finalizado</span>
                                            </button>
                                          ) : (
                                            <button
                                              onClick={() => setMatchToDelete(activeMatch)}
                                              className="flex-1 sm:flex-none flex items-center justify-center px-2.5 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg transition-all shadow-[0_0_10px_rgba(239,68,68,0.1)] sm:min-w-[90px] cursor-pointer"
                                            >
                                              <span className="text-[9px] font-black uppercase tracking-wider flex items-center gap-1"><Users size={10} /> Quitar Publicacion</span>
                                            </button>
                                          )
                                        ) : null
                                      ) : null}
                                      <button
                                        onClick={() => handleDeclineAssistance(item)}
                                        className="flex-1 sm:flex-none flex items-center justify-center px-2.5 py-1 bg-[#363a4d] hover:bg-red-500/10 text-zinc-200 hover:text-red-400 border border-white/10 hover:border-red-500/30 rounded-lg transition-all group/opt sm:min-w-[90px]"
                                      >
                                        <span className="text-[9px] font-black uppercase tracking-wider">Cancelar Turno</span>
                                      </button>
                                      <button
                                        onClick={() => handleCancelFixedBooking(item)}
                                        className="flex-1 sm:flex-none flex items-center justify-center px-2.5 py-1 bg-[#363a4d] hover:bg-red-500/25 text-red-400 hover:text-red-300 border border-red-500/20 hover:border-red-500/50 rounded-lg transition-all group/opt sm:min-w-[90px]"
                                      >
                                        <span className="text-[9px] font-black uppercase tracking-wider">Baja Fijo</span>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );

                            })}
                          </div>
                        </div>
                      )}

                      {turnosActivos.length > 0 && (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 px-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-[var(--primary-color)] animate-pulse transform-gpu shrink-0" />
                            <h3 className="text-xs font-black uppercase tracking-widest text-zinc-300">Turnos Activos</h3>
                            <div className="h-px bg-white/5 flex-1 ml-2" />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 justify-items-center w-full px-1.5 sm:px-2 mt-2">
                            {turnosActivos.slice(0, 6).map(renderTurnoCard)}
                          </div>
                        </div>
                      )}

                      {turnosHistorial.length > 0 && (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 px-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-zinc-600 shrink-0" />
                            <h3 className="text-xs font-black uppercase tracking-widest text-zinc-400">Historial de turnos</h3>
                            <div className="h-px bg-white/5 flex-1 ml-2" />
                          </div>
                          <div className="flex flex-col gap-3 sm:gap-4 px-1.5 sm:px-2 mt-2">
                            {turnosHistorial.slice(0, 3).map(renderHistorialCard)}
                          </div>

                          {turnosHistorial.length > 3 && (
                            <div className="flex justify-center pt-1">
                              <button
                                onClick={() => setShowAllHistoryModal(true)}
                                className="px-6 py-2 bg-[#222530] hover:bg-[#2d313f] text-[10px] font-black uppercase tracking-widest text-zinc-300 hover:text-white rounded-xl border border-[#2d313f] hover:border-[#3e4357] transition-all flex items-center gap-2 shadow-md hover:scale-[1.02] active:scale-95"
                              >
                                Ver Todo el Historial ({turnosHistorial.length})
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL DE HISTORIAL COMPLETO */}
      {showAllHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-300" onClick={() => setShowAllHistoryModal(false)}>
          <div
            className="w-full max-w-2xl bg-[#1a1c23] border border-[#2d313f] rounded-2xl overflow-hidden shadow-2xl relative animate-in zoom-in-95 duration-300 flex flex-col max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-5 bg-[#222530] border-b border-[#2d313f] flex items-center justify-between shrink-0">
              <h3 className="text-sm font-black uppercase tracking-wider text-white flex items-center gap-2">
                <History size={16} className="text-[var(--primary-color)]" /> Registro de Turnos
              </h3>
              <button
                onClick={() => setShowAllHistoryModal(false)}
                className="text-zinc-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5 transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* Lista simple */}
            <div className="overflow-y-auto p-2 sm:p-4 space-y-2 relative flex-1 custom-scrollbar">
              {combinedTurnos
                .filter(item => (item.type === 'booking' && (item.status === 'paid' || item.status === 'cancelled')) || (item.type === 'request' && (item as any).status === 'rejected'))
                .map(renderHistorialCard)}
            </div>
          </div>
        </div>
      )}

      {/* 5. MODAL MULTI-OPCIÓN (DUAL SELECTOR PARA CREAR PETICIÓN) */}
      {selectedSlot && schedule && (
        <div
          onClick={closeModalAndReleaseLock}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-in fade-in duration-300 cursor-pointer"
        >
          <div
            className="w-full max-w-md bg-[#1a1c23] border border-[#2d313f] rounded-2xl overflow-hidden shadow-2xl relative animate-in zoom-in-95 duration-300 cursor-default"
            onClick={(e) => e.stopPropagation()}
          >

            {/* Header del Modal */}
            <div className="p-4 bg-[#1a1c23] flex flex-col items-center justify-center text-center relative border-b border-white/5">
              <h3 className="text-sm font-black uppercase tracking-wider text-white">Solicitar Turno</h3>
              <button
                onClick={closeModalAndReleaseLock}
                className="absolute right-4 top-4 text-zinc-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5 transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* Formulario */}
            <div className="p-5 space-y-4">

              {/* Turno Seleccionado y Precio en una sola fila */}
              {(() => {
                const isPromo = selectedSlot.booking?.status === 'promo';
                const priceInfo = getSlotPriceAndDiscount(selectedSlot.court, selectedSlot.time, selectedDate);
                
                let originalVal = priceInfo.originalPrice;
                let finalVal = priceInfo.finalPrice;
                let discountLabel = priceInfo.discount ? (priceInfo.discount.discountType === 'percentage' ? `${priceInfo.discount.value}% OFF` : `$${priceInfo.discount.value} OFF`) : '';
                
                if (isPromo) {
                  const promoDiscount = selectedSlot.booking?.promoDiscountPercentage || 0;
                  originalVal = priceInfo.originalPrice;
                  if (promoDiscount > 0) {
                    finalVal = Math.max(0, originalVal * (1 - promoDiscount / 100));
                    discountLabel = `${promoDiscount}% PROMO`;
                  } else {
                    finalVal = originalVal;
                    discountLabel = '';
                  }
                }

                return (
                  <div className="bg-[#15171d] border border-[#2d313f] rounded-xl p-4 w-full shadow-inner space-y-3">
                    {/* Date and Time (Más destacado) */}
                    <div className="flex items-center justify-between border-b border-white/5 pb-3">
                      <div className="flex flex-col">
                        <span className="text-zinc-500 text-[9px] font-bold uppercase tracking-wider mb-0.5">Fecha y Día</span>
                        <div className="text-white text-[13px] font-black uppercase flex items-center gap-1.5">
                           <CalendarDays size={14} className="text-theme-primary" />
                           {getFormattedDateWithDay(selectedDate)}
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-zinc-500 text-[9px] font-bold uppercase tracking-wider mb-0.5">Hora</span>
                        <div className="text-theme-primary text-[15px] font-black uppercase">
                           {selectedSlot.time}
                        </div>
                      </div>
                    </div>

                    {/* Club, Court, Price */}
                    <div className="flex items-center justify-between pt-1">
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col">
                           <span className="text-zinc-500 text-[8px] font-bold uppercase tracking-wider mb-0.5">Club</span>
                           <span className="text-zinc-300 text-[11px] font-bold uppercase max-w-[85px] truncate" title={getClubName(selectedSedeId)}>
                             {getClubName(selectedSedeId)}
                           </span>
                        </div>
                        <div className="w-px h-5 bg-white/10 mx-0.5"></div>
                        <div className="flex flex-col">
                           <span className="text-zinc-500 text-[8px] font-bold uppercase tracking-wider mb-0.5">Cancha</span>
                           <span className="text-white font-black text-[11px] uppercase">{selectedSlot.court}</span>
                        </div>
                      </div>

                      <div className="flex flex-col items-end text-right">
                        <span className="text-zinc-500 text-[8px] font-bold uppercase tracking-wider mb-0.5">Valor</span>
                        {discountLabel ? (
                          <>
                            <div className="flex items-center gap-1 mb-0.5">
                              <span className={`text-[7px] font-black uppercase px-1 py-0.5 rounded ${isPromo ? 'bg-purple-500/20 border border-purple-500/30 text-purple-300' : 'bg-emerald-500/15 border border-emerald-500/25 text-emerald-400'}`}>
                                {discountLabel}
                              </span>
                              <span className="text-zinc-500 line-through text-[8px] font-bold">
                                ${originalVal.toLocaleString('es-AR')}
                              </span>
                            </div>
                            <span className="text-white font-black text-[13px]">
                              ${finalVal.toLocaleString('es-AR')}
                            </span>
                          </>
                        ) : (
                          <span className="text-white font-black text-[13px]">
                            ${originalVal.toLocaleString('es-AR')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Promo Details Card */}
              {selectedSlot.booking?.status === 'promo' && (
                <div className="bg-[#1a1429] border border-purple-500/25 rounded-xl p-3.5 space-y-2.5 relative overflow-hidden shadow-lg text-center">
                  <div className="absolute top-0 right-0 bg-purple-500/20 text-purple-300 text-[7px] font-black tracking-widest px-2 py-0.5 rounded-bl uppercase">
                    ¡BENEFICIO EXCLUSIVO!
                  </div>
                  <div className="flex flex-col items-center justify-center">
                    <span className="text-purple-400 text-[10px] font-black uppercase tracking-wider flex items-center gap-1 justify-center">
                      ⚡ Turno Promocionado
                    </span>
                    {(selectedSlot.booking.promoPerks?.drink || selectedSlot.booking.promoPerks?.balls || selectedSlot.booking.promoPerks?.winnerPrize) && (
                      <p className="text-[9px] text-zinc-400 font-bold mt-0.5">Este turno incluye las siguientes cortesías de regalo:</p>
                    )}
                  </div>
                  <div className="flex flex-wrap justify-center gap-1.5 pt-0.5 w-full">
                    {selectedSlot.booking.promoPerks?.drink && (
                      <span className="flex items-center gap-1 bg-purple-500/10 border border-purple-500/20 text-purple-300 text-[8px] font-black uppercase px-2 py-1 rounded-lg shrink-0">
                        🥤 Bebida Gratis
                      </span>
                    )}
                    {selectedSlot.booking.promoPerks?.balls && (
                      <span className="flex items-center gap-1 bg-purple-500/10 border border-purple-500/20 text-purple-300 text-[8px] font-black uppercase px-2 py-1 rounded-lg shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" className="drop-shadow-md"><circle cx="12" cy="12" r="10" fill="#ccff00" stroke="#9acd00" strokeWidth="1"/><path d="M6 5.3a9 9 0 0 1 0 13.4" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/><path d="M18 5.3a9 9 0 0 0 0 13.4" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/></svg> Pelotas Nuevas
                      </span>
                    )}
                    {selectedSlot.booking.promoPerks?.winnerPrize && (
                      <span className="flex items-center gap-1 bg-purple-500/10 border border-purple-500/20 text-purple-300 text-[8px] font-black uppercase px-2 py-1 rounded-lg shrink-0">
                        🏆 Premio Ganador
                      </span>
                    )}
                  </div>
                </div>
              )}





              {/* Declaraciones Obligatorias de Responsabilidad */}
              <div className="bg-[#15171d]/50 border border-[#2d313f] rounded-lg p-3 flex items-center gap-3">
                <span className="text-[10px] text-zinc-400 leading-snug flex-1">
                  Reserva solo si estás seguro. Cancelaciones con menos de 24hs de anticipación pueden requerir el abono completo del turno.
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setCheckCancelPolicy(true);
                    setCheckAdminCourt(true);
                  }}
                  className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase shrink-0 transition-all ${
                    checkCancelPolicy 
                      ? 'bg-transparent text-emerald-500 border border-emerald-500/30' 
                      : 'bg-[#222530] text-zinc-300 border border-[#2d313f] hover:bg-[#2d313f] active:scale-95'
                  }`}
                >
                  {checkCancelPolicy ? 'Aceptado' : 'Entiendo'}
                </button>
              </div>

              {/* Botón de Envío */}
              <button
                type="button"
                onClick={() => {
                  if (!currentUser?.uid) {
                    setPendingBookingSubmit(true);
                    onRequireLogin?.();
                    return;
                  }
                  const isPhoneVerified = currentPlayerProfile?.phoneVerified || (currentUser as any)?.phoneVerified || false;
                  if (!isPhoneVerified) {
                    setShowPhoneModal(true);
                  } else {
                    handleCreateRequest();
                  }
                }}
                disabled={!checkAdminCourt || !checkCancelPolicy || sendingRequest}
                className={`w-full font-black uppercase py-3.5 rounded-xl hover:scale-[1.01] active:scale-95 disabled:opacity-50 transition-all text-[11px] tracking-wider shadow-[0_4px_15px_rgba(var(--glow-rgb),0.2)] mt-2 flex items-center justify-center gap-1.5 ${(getRequiredDepositPercentage(schedule?.paymentConfig, selectedDate, selectedSlot?.time || '') > 0) ? 'bg-[#009EE3] hover:bg-[#0087c2] text-white shadow-[#009EE3]/30 border border-[#009EE3]' : 'theme-primary-bg text-black'}`}
              >
                {sendingRequest ? (
                  <div className={`w-4 h-4 border-2 border-t-transparent rounded-full animate-spin ${(getRequiredDepositPercentage(schedule?.paymentConfig, selectedDate, selectedSlot?.time || '') > 0) ? 'border-white' : 'border-zinc-700 border-t-black'}`} />
                ) : (
                  (getRequiredDepositPercentage(schedule?.paymentConfig, selectedDate, selectedSlot?.time || '') > 0) ? (
                    <div className="flex items-center justify-center gap-2.5">
                      <div className="w-[28px] h-[28px] bg-white rounded-full flex items-center justify-center shrink-0 overflow-hidden shadow-sm">
                        <img src="/mp-logo.png" alt="Mercado Pago" className="w-[20px] h-[20px] object-contain" />
                      </div>
                      <div className="flex flex-col items-start text-left">
                        <span className="text-[12px] leading-tight">Reservar con Seña</span>
                        <span className="text-[10px] font-black opacity-100 normal-case tracking-wide text-white drop-shadow-md leading-tight">
                          Abonar: ${(() => {
                            const isPromo = selectedSlot?.booking?.status === 'promo';
                            const pInfo = getSlotPriceAndDiscount(selectedSlot?.court || '', selectedSlot?.time || '', selectedDate);
                            let fVal = pInfo.finalPrice;
                            if (isPromo) {
                              const promoDiscount = selectedSlot?.booking?.promoDiscountPercentage || 0;
                              if (promoDiscount > 0) fVal = Math.max(0, pInfo.originalPrice * (1 - promoDiscount / 100));
                            }
                            const percentage = getRequiredDepositPercentage(schedule?.paymentConfig, selectedDate, selectedSlot?.time || '');
                            return Math.round(fVal * (percentage / 100)).toLocaleString('es-AR');
                          })()}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <CheckCircle2 size={14} />
                      <span>Enviar Solicitud de Turno</span>
                    </>
                  )
                )}
              </button>

            </div>

          </div>
        </div>
      )}

      {/* 5.5A. MODAL DE VERIFICACIÓN DE WHATSAPP */}
      <WhatsAppVerificationModal
        isOpen={showPhoneModal}
        onClose={() => setShowPhoneModal(false)}
        onSuccess={() => {
          setShowPhoneModal(false);
          handleCreateRequest();
        }}
        config={config}
      />



      {/* 5.6. MODAL: CANCELAR TURNO INDIVIDUAL (PROYECTADO) */}
      {showDeclineModal && targetFixedTurn && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 animate-in fade-in duration-300">
          <div className="w-full max-w-sm bg-[#222530] border border-amber-500/20 rounded-2xl overflow-hidden shadow-2xl relative p-6 space-y-5 animate-in zoom-in-95 duration-300 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/25 flex items-center justify-center text-amber-500">
              <X size={20} className="stroke-[3]" />
            </div>
            <div className="space-y-1.5 text-center">
              <h3 className="text-base font-black uppercase tracking-wider text-white">¿Cancelar este turno?</h3>
              <p className="text-zinc-400 text-[10px] leading-relaxed">
                Si confirmas, se avisará al club que <span className="text-white font-bold">no asistirás</span> únicamente en la fecha seleccionada. El resto de tus turnos fijos semanales se mantendrán sin cambios.
              </p>
            </div>
            <div className="bg-[#15171d] border border-[#2d313f] rounded-xl p-4 space-y-2 text-center text-[10px]">
              <div className="flex flex-col items-center">
                <span className="text-[7px] text-zinc-500 uppercase tracking-widest font-bold">Fecha a cancelar</span>
                <span className="text-amber-400 font-black mt-0.5">{getFormattedDateWithDay(targetFixedTurn.date)}</span>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => { setShowDeclineModal(false); setTargetFixedTurn(null); }}
                className="flex-1 bg-[#15171d] hover:bg-white/5 text-zinc-400 hover:text-white border border-[#2d313f] font-black uppercase py-3.5 rounded-xl text-[10px] tracking-wider transition-all"
              >
                Volver
              </button>
              <button
                onClick={confirmDeclineAssistance}
                disabled={loading}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-black uppercase py-3.5 rounded-xl transition-all text-[10px] tracking-wider shadow-lg shadow-amber-500/10"
              >
                Confirmar Baja
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 5.7. MODAL: DAR DE BAJA TURNO FIJO (PERMANENTE) */}
      {showCancelFixedModal && targetFixedTurn && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 animate-in fade-in duration-300">
          <div className="w-full max-w-sm bg-[#222530] border border-amber-500/20 rounded-2xl overflow-hidden shadow-2xl relative p-6 space-y-5 animate-in zoom-in-95 duration-300 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/25 flex items-center justify-center text-amber-500">
              <Trash2 size={20} className="stroke-[3]" />
            </div>
            <div className="space-y-1.5 text-center">
              <h3 className="text-base font-black uppercase tracking-wider text-white">Dar de baja Turno Fijo</h3>
              <p className="text-zinc-400 text-[10px] leading-relaxed">
                ¿Estás seguro de que deseas eliminar tu prioridad semanal? <span className="text-amber-400 font-bold">Perderás tu lugar asegurado</span> para las próximas semanas y el turno volverá a estar disponible para otros jugadores.
              </p>
            </div>
            <div className="bg-[#15171d] border border-[#2d313f] rounded-xl p-4 space-y-2 text-center text-[10px]">
              <div className="flex flex-col items-center">
                <span className="text-[7px] text-zinc-500 uppercase tracking-widest font-bold">Turno a liberar</span>
                <span className="text-white font-black mt-0.5">{targetFixedTurn.courtName} — {targetFixedTurn.timeSlot}</span>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => { setShowCancelFixedModal(false); setTargetFixedTurn(null); }}
                className="flex-1 bg-[#15171d] hover:bg-white/5 text-zinc-400 hover:text-white border border-[#2d313f] font-black uppercase py-3.5 rounded-xl text-[10px] tracking-wider transition-all"
              >
                Mantener Turno
              </button>
              <button
                onClick={confirmCancelFixedBooking}
                disabled={loading}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-black uppercase py-3.5 rounded-xl transition-all text-[10px] tracking-wider shadow-lg shadow-amber-500/10"
              >
                Confirmar Baja
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMACIÓN PARA QUITAR PUBLICACIÓN */}
      {matchToDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 animate-in fade-in duration-300">
          <div className="w-full max-w-sm bg-[#222530] border border-red-500/20 rounded-2xl overflow-hidden shadow-2xl relative p-6 space-y-5 animate-in zoom-in-95 duration-300 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto w-12 h-12 rounded-full bg-red-500/10 border border-red-500/25 flex items-center justify-center text-red-500">
              <Trash2 size={20} className="stroke-[3]" />
            </div>

            <div className="space-y-1.5 text-center">
              <h3 className="text-base font-black uppercase tracking-wider text-white">Quitar Publicación</h3>
              <p className="text-zinc-400 text-xs text-center leading-relaxed">
                ¿Estás seguro de que deseas eliminar este partido publicado? Otros jugadores ya no podrán unirse.
              </p>
            </div>

            {/* Ficha Resumen */}
            <div className="bg-[#15171d] border border-[#2d313f] rounded-xl p-4 text-center text-xs space-y-2">
              <div className="flex flex-col items-center">
                <span className="text-[8px] text-zinc-500 uppercase tracking-widest font-bold">Club / Sede</span>
                <span className="text-white font-black text-xs mt-0.5">{matchToDelete.clubName}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/5">
                <div className="flex flex-col items-center">
                  <span className="text-[8px] text-zinc-500 uppercase tracking-widest font-bold">Fecha</span>
                  <span className="text-zinc-200 font-bold mt-0.5">{matchToDelete.date.split('-').reverse().join('/')}</span>
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-[8px] text-zinc-500 uppercase tracking-widest font-bold">Horario</span>
                  <span className="text-[var(--primary-color)] font-black mt-0.5">{matchToDelete.time}</span>
                </div>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setMatchToDelete(null)}
                className="flex-1 bg-[#15171d] hover:bg-white/5 text-zinc-400 hover:text-white border border-[#2d313f] font-black uppercase py-3 rounded-xl text-[10px] tracking-wider transition-all cursor-pointer"
              >
                Volver
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await deleteDoc(doc(db, getCollectionPath('public_matches'), matchToDelete.id));
                    setMatchToDelete(null);
                  } catch (e) {
                    console.error("Error al eliminar public_match:", e);
                    alert("Hubo un error al quitar la publicación.");
                  }
                }}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-black uppercase py-3 rounded-xl transition-all text-[10px] tracking-wider shadow-lg shadow-red-500/10 cursor-pointer"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedBookingForMatch && (
        <PublicMatchCreatorModal
          isOpen={!!selectedBookingForMatch}
          onClose={() => setSelectedBookingForMatch(null)}
          onCreated={(matchId) => {
            setSelectedBookingForMatch(null);
            window.dispatchEvent(new Event('switch-to-partidos'));
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('openPublicMatch', { detail: { matchId: matchId, onlyHighlight: true } }));
            }, 300);
          }}
          booking={{
            id: selectedBookingForMatch.id,
            clubId: selectedBookingForMatch.clubId,
            clubName: getClubName(selectedBookingForMatch.clubId),
            date: selectedBookingForMatch.date,
            time: selectedBookingForMatch.timeSlot?.split(' - ')[0] || selectedBookingForMatch.timeSlot,
            endTime: selectedBookingForMatch.timeSlot?.split(' - ')[1] || '00:00',
            courtId: selectedBookingForMatch.courtId || selectedBookingForMatch.courtName,
            courtName: selectedBookingForMatch.courtName,
            totalPrice: selectedBookingForMatch.price || 0
          }}
          currentUser={{
            uid: currentUser.uid,
            isProfileValidated: !!currentPlayerProfile?.isValidated,
            category: currentPlayerProfile?.category
          }}
          config={config}
        />
      )}
    </div>
  );
}
